/**
 * WmsPageRenderer — Generic DSL-driven page renderer for all WMS screens.
 *
 * Takes a WmsPageDef and renders:
 *  - KPI cards
 *  - Tabbed tables with dynamic columns
 *  - Status badges from the shared status map
 *  - Row action buttons that call APIs
 *  - Create dialogs with dynamic forms
 *  - Search + barcode scanning
 *
 * Zero hardcoded business logic — everything comes from the DSL definition.
 */

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useRenderMode } from '@/hooks/useRenderMode';
import { useRenderedTab } from '@/hooks/usePageParams';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  Loader2, RefreshCcw, Plus, Minus, Search, ScanLine, CheckCircle2,
  AlertCircle, Package, Truck, ArrowDownLeft, Layers, ClipboardList,
  Calendar, Clock, Activity, BarChart3, Send, Play, X, Container,
  PackageCheck, LayoutList, MapPin, Grid3X3, Database, ArrowRight, Lock, BookCopy,
  ChevronUp, ChevronDown, Pencil, Upload, Trash2, Download, Printer,
} from 'lucide-react';
import { api, BASE, getToken } from '@/lib/api';
import { formatStatusText, formatValuePure, summarizeValue } from '@/lib/format';
import {
  applyWmsBillingModelToTabs,
  applyWmsReportModelToTabs,
  applyWmsWorkspaceTemplateToTabs,
  buildWmsRuntimePageContext,
  buildWmsTemplateResolutionContextFromSearchParams,
  type WmsRuntimeBillingModel,
  type WmsRuntimeReportModel,
  type WmsRuntimeWorkspaceTemplate,
} from '@/lib/wms-template-config';
import { useWmsProfile } from '@/pages/wms/useWmsProfile';
import { WmsInboundOrderDetailPanel } from '@/pages/wms/WmsInboundOrderDetailPage';
import { WmsIssueOrderDetailPanel } from '@/pages/wms/WmsIssueOrderDetailPage';
import { WmsMaterialRequestDetailPanel } from '@/pages/wms/WmsMaterialRequestDetailPage';
import { WmsPurchaseOrderDetailPanel } from '@/pages/wms/WmsPurchaseOrderDetailPage';
import { WmsQcRecordDetailPanel } from '@/pages/wms/WmsQcRecordDetailPage';
import { WmsQcInboundDetailPanel } from '@/pages/wms/WmsQcInboundDetailPage';
import { WmsCycleCountDetailPanel } from '@/pages/wms/WmsCycleCountDetailPage';
import { WmsReceivingStickerPanel } from './WmsReceivingStickerPanel';
import { WmsImportWorkflowSheet, getImportButtonLabel } from './WmsImportWorkflowSheet';
import { DocumentCopySheet, type DocumentCopyResult, type DocumentCopySourceDocument } from '@/components/document-copy/DocumentCopySheet';
import type {
  WmsPageDef, TabDef, KpiCardDef, ColumnDef, ActionDef, DialogDef, StatusDef, FormFieldDef,
  FilterDef, BatchActionDef,
} from './wms-page-dsl';
import { getStatusDef } from './wms-page-dsl';

// ─── Icon Map ──────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  Package, Truck, ArrowDownLeft, Layers, ClipboardList, Calendar, Clock,
  Activity, BarChart3, Send, Play, X, Container, PackageCheck, LayoutList,
  MapPin, Grid3X3: Grid3X3, Database, ArrowRight, Lock, ScanLine,
  Loader2, RefreshCcw, Plus, Search, CheckCircle2, AlertCircle, Download, Printer, Warehouse: Container,
};

function getIcon(name: string | undefined) {
  if (!name) return null;
  return ICON_MAP[name] || null;
}

// ─── Format helpers ────────────────────────────────────────────────────────

function formatValue(val: unknown, format?: ColumnDef['format']): string {
  return formatValuePure(val, format);
}

function interpolateRowPath(template: string, row: Record<string, unknown>): string {
  return template.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => {
    const value = key === 'id' ? row.id : row[key];
    return encodeURIComponent(String(value ?? ''));
  });
}

function canonicalizeWmRoute(route: string): string {
  if (!route.startsWith('/wms')) return route;
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const targetBase = pathname === '/wp' || pathname.startsWith('/wp/') ? '/wp' : '/wm';
  return route.replace(/^\/wms/, targetBase);
}

function resolveLegacyTabKey(pageKey: string, tabKey: string | null): string | null {
  if (!tabKey) return null;
  if (pageKey === 'inventory') {
    const inventoryAliases: Record<string, string> = {
      allInventory: 'allMaterialStock',
      stock: 'pipeMaterialStock',
      plateInventory: 'plateMaterialStock',
    };
    return inventoryAliases[tabKey] ?? tabKey;
  }
  return tabKey;
}

const WP_SIMPLE_OPS_PAGES = new Set([
  'purchaseOrders',
  'inbound',
  'inspection',
  'materialRequests',
  'issueOrders',
  'projectReturns',
  'outbound',
  'inventory',
  'stockLedger',
]);

type WmsRendererActionOutcome = {
  actionId: string;
  actionType: 'upload' | 'download' | 'export' | 'batch' | 'delete' | 'post' | 'custom';
  outcome: 'succeeded' | 'failed' | 'blocked' | 'degraded' | 'rolled_back';
  entityType?: string;
  entityId?: string;
  selectedEntityRefs?: Array<{ type: string; id: string; label?: string }>;
  summary?: string;
  errorCode?: string;
  errorMessage?: string;
  rollbackHint?: string;
  durationMs?: number;
  metadata?: Record<string, unknown>;
};

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function inferWmsActionType(input: { key?: string; label?: string; method?: string }): WmsRendererActionOutcome['actionType'] {
  const text = `${input.key ?? ''} ${input.label ?? ''}`.toLowerCase();
  if (input.method === 'DELETE' || text.includes('delete') || text.includes('remove')) return 'delete';
  if (text.includes('download') || text.includes('open') || text.includes('view')) return 'download';
  if (text.includes('export')) return 'export';
  if (text.includes('post') || text.includes('approve') || text.includes('submit')) return 'post';
  return 'custom';
}

async function recordWmsRendererActionOutcome(outcome: WmsRendererActionOutcome): Promise<void> {
  try {
    await api.post('/api/runtime/action/outcome', {
      module: 'wms',
      route: typeof window !== 'undefined' ? canonicalizeWmRoute(window.location.pathname) : '/wm',
      surface: 'web',
      selectedEntityRefs: outcome.selectedEntityRefs ?? (outcome.entityType && outcome.entityId
        ? [{ type: outcome.entityType, id: outcome.entityId }]
        : []),
      ...outcome,
      metadata: {
        source: 'WmsPageRenderer',
        executionMode: 'production-handler',
        ...outcome.metadata,
      },
    });
  } catch (err) {
    console.warn('Failed to record WMS runtime action outcome', err);
  }
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((o: any, k) => o?.[k], obj);
}

function parseJsonObject(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

type WmsDocumentCopyConfig = {
  targetLabel: string;
  duplicatePath: (rowId: string) => string;
  endpoints: {
    listPath: string;
    detailPath: string;
    copyPath: string;
  };
};

function getWmsDocumentCopyConfig(tab: TabDef): WmsDocumentCopyConfig | null {
  const detailPath = tab.detailApiPath ?? '';
  if (detailPath.includes('/scm/quotations/:id') || detailPath.includes('/quotations/:id')) {
    return {
      targetLabel: 'quotation',
      duplicatePath: (rowId) => `/quotations/${rowId}/duplicate`,
      endpoints: {
        listPath: '/quotations/copy-sources',
        detailPath: '/quotations/copy-source-detail',
        copyPath: '/quotations/copy',
      },
    };
  }
  if (detailPath.includes('/scm/sales-orders/:id')) {
    return {
      targetLabel: 'sales order',
      duplicatePath: (rowId) => `/scm/sales-orders/${rowId}/duplicate`,
      endpoints: {
        listPath: '/scm/sales-orders/copy-sources',
        detailPath: '/scm/sales-orders/copy-source-detail',
        copyPath: '/scm/sales-orders/copy',
      },
    };
  }
  if (detailPath.includes('/scm/purchase-orders/:id')) {
    return {
      targetLabel: 'purchase order',
      duplicatePath: (rowId) => `/scm/purchase-orders/${rowId}/duplicate`,
      endpoints: {
        listPath: '/scm/purchase-orders/copy-sources',
        detailPath: '/scm/purchase-orders/copy-source-detail',
        copyPath: '/scm/purchase-orders/copy',
      },
    };
  }
  return null;
}

// ─── Sort Helpers ──────────────────────────────────────────────────────────

type SortDirection = 'asc' | 'desc';
interface SortState { field: string | null; direction: SortDirection }

function sortRows(
  rows: Record<string, unknown>[],
  sort: SortState,
  columns: ColumnDef[],
): Record<string, unknown>[] {
  if (!sort.field) return rows;
  const col = columns.find(c => c.field === sort.field);
  if (!col) return rows;
  const fmt = col.format;
  const sorted = [...rows].sort((a, b) => {
    const va = a[sort.field!];
    const vb = b[sort.field!];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    // date/datetime
    if (fmt === 'date' || fmt === 'datetime') {
      return new Date(String(va)).getTime() - new Date(String(vb)).getTime();
    }
    // number/currency
    if (fmt === 'number' || fmt === 'currency') {
      return (Number(va) || 0) - (Number(vb) || 0);
    }
    // try numeric first
    const na = Number(va), nb = Number(vb);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return summarizeValue(va).localeCompare(summarizeValue(vb));
  });
  return sort.direction === 'desc' ? sorted.reverse() : sorted;
}

function getEditableField(tab: TabDef, fieldName: string): FormFieldDef | undefined {
  return tab.editFields?.find((field) => field.field === fieldName);
}

function buildOptions(
  field: FormFieldDef,
  dynamicOptions: Record<string, Array<{ label: string; value: string }>>,
): Array<{ label: string; value: string }> {
  return field.options ?? dynamicOptions[field.field] ?? [];
}

function buildFilterOptions(
  filter: FilterDef,
  dynamicOptions: Record<string, Array<{ label: string; value: string }>>,
): Array<{ label: string; value: string }> {
  return filter.options ?? dynamicOptions[filter.field] ?? [];
}

function createInitialFilterValues(filters?: FilterDef[]): Record<string, string> {
  if (!filters || filters.length === 0) return {};

  return Object.fromEntries(filters.flatMap((filter) => {
    if (filter.type === 'daterange') {
      return [[`${filter.field}From`, ''], [`${filter.field}To`, '']];
    }
    return [[filter.field, '']];
  }));
}

function buildTabRequestParams(
  tab: TabDef,
  filters: FilterDef[] | undefined,
  filterValues: Record<string, string>,
): Record<string, string | number> {
  const params: Record<string, string | number> = { ...(tab.apiParams ?? {}) };

  for (const filter of filters ?? []) {
    if (filter.type === 'daterange') {
      const fromValue = filterValues[`${filter.field}From`]?.trim();
      const toValue = filterValues[`${filter.field}To`]?.trim();
      if (fromValue) params[`${filter.field}From`] = fromValue;
      if (toValue) params[`${filter.field}To`] = toValue;
      continue;
    }

    if (filter.type === 'multiselect') {
      continue;
    }

    const value = filterValues[filter.field]?.trim();
    if (value) params[filter.field] = value;
  }

  return params;
}

function applyClientFilters(
  rows: Record<string, unknown>[],
  filters: FilterDef[] | undefined,
  filterValues: Record<string, string>,
  tab: TabDef,
): Record<string, unknown>[] {
  let nextRows = rows;

  for (const filter of filters ?? []) {
    if (filter.type !== 'multiselect') continue;
    const selected = (filterValues[filter.field] ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    if (selected.length === 0) continue;

    const selectedSet = new Set(selected.map((value) => value.toUpperCase()));
    const fallbackField = filter.field === 'status' ? (tab.statusField ?? 'status') : filter.field;
    nextRows = nextRows.filter((row) => {
      const value = String(row[filter.field] ?? row[fallbackField] ?? '').toUpperCase();
      return selectedSet.has(value);
    });
  }

  return nextRows;
}

function resolveOptionsEndpointTemplate(
  endpoint: string,
  values: Record<string, string | string[]>,
): string | null {
  let missing = false;
  const resolved = endpoint.replace(/\{\{([^}]+)\}\}/g, (_, rawKey: string) => {
    const key = rawKey.trim();
    const value = values[key];
    const normalized = Array.isArray(value) ? value[0] : value;
    if (!normalized) {
      missing = true;
      return '';
    }
    return encodeURIComponent(normalized);
  });
  return missing ? null : resolved;
}

function normalizeOptionsEndpoint(endpoint: string): string {
  if (!endpoint.startsWith('/wms/skus')) return endpoint;

  const [path, queryString = ''] = endpoint.split('?');
  const params = new URLSearchParams(queryString);
  if (!params.has('limit')) params.set('limit', '50');
  return `${path}?${params.toString()}`;
}

function buildOptionsRequestUrl(
  endpoint: string,
  options?: {
    search?: string;
    limit?: number;
    page?: number;
  },
): string {
  const normalized = normalizeOptionsEndpoint(endpoint);
  const [path, queryString = ''] = normalized.split('?');
  const params = new URLSearchParams(queryString);
  if (options?.limit != null && !params.has('limit')) params.set('limit', String(options.limit));
  if (options?.page != null && !params.has('page')) params.set('page', String(options.page));
  const search = options?.search?.trim();
  if (search) params.set('search', search);
  return `${path}?${params.toString()}`;
}

async function fetchOptionItems(
  endpoint: string,
  map: { label: string; value: string },
  options?: {
    search?: string;
    limit?: number;
    page?: number;
  },
): Promise<Array<{ label: string; value: string }>> {
  const res = await api.get<any>(buildOptionsRequestUrl(endpoint, options));
  const items = Array.isArray(res) ? res : (res?.items ?? []);
  return items.map((item: Record<string, unknown>) => ({
    label: String(item[map.label] ?? item.code ?? item.id ?? ''),
    value: String(item[map.value] ?? item.id ?? ''),
  }));
}

function getDialogGridClass(columns = 6): string {
  if (columns >= 6) return 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6';
  if (columns === 4) return 'grid gap-3 sm:grid-cols-2 xl:grid-cols-4';
  if (columns === 3) return 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3';
  if (columns === 2) return 'grid gap-3 sm:grid-cols-2';
  return 'space-y-3';
}

function getFieldSpanClass(field: FormFieldDef, columns = 6): string {
  const fallbackSpan = field.type === 'textarea' ? columns : 1;
  const span = Math.max(1, Math.min(columns, field.span ?? fallbackSpan));
  if (columns >= 6) {
    return {
      1: 'xl:col-span-1',
      2: 'xl:col-span-2',
      3: 'xl:col-span-3',
      4: 'xl:col-span-4',
      5: 'xl:col-span-5',
      6: 'xl:col-span-6',
    }[span] ?? 'xl:col-span-1';
  }
  if (columns === 4) {
    return {
      1: 'xl:col-span-1',
      2: 'xl:col-span-2',
      3: 'xl:col-span-3',
      4: 'xl:col-span-4',
    }[Math.min(span, 4)] ?? 'xl:col-span-1';
  }
  if (columns === 3) {
    return {
      1: 'xl:col-span-1',
      2: 'xl:col-span-2',
      3: 'xl:col-span-3',
    }[Math.min(span, 3)] ?? 'xl:col-span-1';
  }
  if (columns === 2) {
    return {
      1: 'sm:col-span-1',
      2: 'sm:col-span-2',
    }[Math.min(span, 2)] ?? 'sm:col-span-1';
  }
  return '';
}

function isFieldVisible(field: FormFieldDef, values: Record<string, string | string[]>): boolean {
  if (!field.showWhen) return true;
  const current = values[field.showWhen.field];
  const currentValue = Array.isArray(current) ? current[0] : current;
  return currentValue != null && field.showWhen.values.includes(String(currentValue));
}

function isEmptyFieldValue(value: string | string[] | undefined): boolean {
  if (Array.isArray(value)) return value.length === 0;
  return !value?.trim();
}

function getSingleFieldValue(value: string | string[] | undefined): string {
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
}

function getMultiFieldValue(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value;
  return [];
}

function MultiSelectField({
  field,
  options,
  value,
  onChange,
  loadOptions,
}: {
  field: FormFieldDef;
  options: Array<{ label: string; value: string }>;
  value: string[];
  onChange: (next: string[]) => void;
  loadOptions?: (search: string) => Promise<Array<{ label: string; value: string }>>;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remoteOptions, setRemoteOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !loadOptions) return;
    if (field.preloadOptions === false && query.trim() === '') {
      setRemoteOptions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      void loadOptions(query)
        .then((nextOptions) => {
          if (!cancelled) setRemoteOptions(nextOptions);
        })
        .catch(() => {
          if (!cancelled) setRemoteOptions([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [loadOptions, open, query]);

  const mergedOptions = useMemo(() => {
    const merged = new Map<string, { label: string; value: string }>();
    [...options, ...remoteOptions].forEach((option) => {
      merged.set(option.value, option);
    });
    value.forEach((selectedValue) => {
      if (!merged.has(selectedValue)) {
        merged.set(selectedValue, { label: selectedValue, value: selectedValue });
      }
    });
    return Array.from(merged.values());
  }, [options, remoteOptions, value]);

  const selectedLabels = mergedOptions
    .filter((option) => value.includes(option.value))
    .map((option) => option.label);

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((item) => item !== optionValue));
      return;
    }
    onChange([...value, optionValue]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="mt-1 h-auto min-h-8 w-full justify-between px-3 py-2 text-left text-sm font-normal">
          <span className="truncate">
            {selectedLabels.length > 0
              ? `${selectedLabels.slice(0, 2).join(', ')}${selectedLabels.length > 2 ? ` +${selectedLabels.length - 2}` : ''}`
              : (field.placeholder ?? `Select ${field.label}`)}
          </span>
          <span className="ml-2 shrink-0 text-xs text-muted-foreground">{value.length}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-0">
        <Command>
          <CommandInput
            placeholder={`Search ${field.label.toLowerCase()}...`}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {field.preloadOptions === false && query.trim() === ''
                ? `Type to search ${field.label.toLowerCase()}.`
                : loading
                  ? 'Loading...'
                  : 'No options found.'}
            </CommandEmpty>
            <CommandGroup>
              {mergedOptions.map((option) => {
                const checked = value.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.value}`}
                    onSelect={() => toggleOption(option.value)}
                  >
                    <Checkbox checked={checked} className="pointer-events-none" />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function serializeFieldValue(value: unknown, field: FormFieldDef): string {
  if (value == null) return '';
  if (field.type === 'date') {
    try {
      return new Date(String(value)).toISOString().slice(0, 10);
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

function coerceFieldValue(field: FormFieldDef, raw: string): unknown {
  if (field.type === 'number') {
    if (raw.trim() === '') return undefined;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  if (field.type === 'select') {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  }

  return raw;
}

async function loadFieldOptions(
  fields: FormFieldDef[],
  contextValues: Record<string, string | string[]> = {},
): Promise<Record<string, Array<{ label: string; value: string }>>> {
  const fieldsWithEndpoints = fields.filter((field) => field.optionsEndpoint);
  if (fieldsWithEndpoints.length === 0) return {};

  const entries = await Promise.all(fieldsWithEndpoints.map(async (field) => {
    try {
      const endpoint = resolveOptionsEndpointTemplate(field.optionsEndpoint!, contextValues);
      if (!endpoint) return [field.field, []] as const;
      const map = field.optionsMap ?? { label: 'name', value: 'id' };
      return [
        field.field,
        await fetchOptionItems(endpoint, map, { limit: 50, page: 1 }),
      ] as const;
    } catch {
      return [field.field, []] as const;
    }
  }));

  return Object.fromEntries(entries);
}

async function loadFilterOptions(filters: FilterDef[]): Promise<Record<string, Array<{ label: string; value: string }>>> {
  const filtersWithEndpoints = filters.filter((filter) => filter.optionsEndpoint);
  if (filtersWithEndpoints.length === 0) return {};

  const entries = await Promise.all(filtersWithEndpoints.map(async (filter) => {
    try {
      const res = await api.get<any>(normalizeOptionsEndpoint(filter.optionsEndpoint!));
      const items = Array.isArray(res) ? res : (res?.items ?? []);
      const map = filter.optionsMap ?? { label: 'name', value: 'id' };
      return [
        filter.field,
        items.map((item: Record<string, unknown>) => ({
          label: String(item[map.label] ?? item.code ?? item.id ?? ''),
          value: String(item[map.value] ?? item.id ?? ''),
        })),
      ] as const;
    } catch {
      return [filter.field, []] as const;
    }
  }));

  return Object.fromEntries(entries);
}

// ─── CSV Escape ────────────────────────────────────────────────────────────

function escapeCsv(v: string): string {
  return v.includes(',') || v.includes('"') || v.includes('\n')
    ? `"${v.replace(/"/g, '""')}"`
    : v;
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(cell);
      cell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      continue;
    }

    cell += char ?? '';
  }

  row.push(cell);
  if (row.some((value) => value.trim() !== '') || rows.length === 0) rows.push(row);
  return rows;
}

// ─── StatusBadge ───────────────────────────────────────────────────────────

function StatusBadge({ value, statuses }: { value: string; statuses: StatusDef[] }) {
  const def = getStatusDef(String(value).toUpperCase(), statuses);
  return <Badge variant="outline" className={`text-xs ${def.color}`}>{def.label === def.key ? formatStatusText(value) : def.label}</Badge>;
}

// ─── KPI Cards ─────────────────────────────────────────────────────────────

function KpiCards({ cards, data }: { cards: KpiCardDef[]; data: Record<string, unknown> }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
      {cards.map(card => {
        const Icon = getIcon(card.icon);
        const value = getNestedValue(data, card.valuePath);
        const content = (
          <Card key={card.key} className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-3 flex items-center gap-3">
              {Icon && <Icon className={`h-8 w-8 ${card.color ?? 'text-muted-foreground'}`} />}
              <div>
                <p className="text-xs text-muted-foreground">{card.label}</p>
                <p className="text-xl font-bold">{value != null ? Number(value).toLocaleString() : '0'}</p>
              </div>
            </CardContent>
          </Card>
        );
        return card.link
          ? <Link key={card.key} to={canonicalizeWmRoute(card.link)}>{content}</Link>
          : <div key={card.key}>{content}</div>;
      })}
    </div>
  );
}

// ─── Dynamic Dialog ────────────────────────────────────────────────────────

function DynamicDialog({
  def, open, onClose, onSuccess, statuses,
}: {
  def: DialogDef;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  statuses: StatusDef[];
}) {
  const [values, setValues] = useState<Record<string, string | string[]>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});

  useEffect(() => {
    if (!open) return;
    const initial: Record<string, string | string[]> = {};
    def.fields.forEach((f) => {
      if (Array.isArray(f.defaultValue)) {
        initial[f.field] = [...f.defaultValue];
      } else if (f.type === 'multiselect') {
        initial[f.field] = [];
      } else {
        initial[f.field] = f.defaultValue ?? '';
      }
    });
    setValues(initial);
    setError('');
    setSuccess('');
    setDynamicOptions({});
  }, [open, def]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fieldsWithEndpoints = def.fields.filter((field) => field.optionsEndpoint && field.preloadOptions !== false);
    if (fieldsWithEndpoints.length === 0) return;

    void Promise.all(fieldsWithEndpoints.map(async (field) => {
      const endpoint = resolveOptionsEndpointTemplate(field.optionsEndpoint!, values);
      if (!endpoint) return [field.field, []] as const;
      try {
        const map = field.optionsMap ?? { label: 'name', value: 'id' };
        return [
          field.field,
          await fetchOptionItems(endpoint, map, { limit: 50, page: 1 }),
        ] as const;
      } catch {
        return [field.field, []] as const;
      }
    })).then((entries) => {
      if (!cancelled) setDynamicOptions(Object.fromEntries(entries));
    });

    return () => { cancelled = true; };
  }, [open, def.fields, values]);

  const handleSubmit = async () => {
    const activeFields = def.fields.filter((field) => isFieldVisible(field, values));
    const visibleFields = activeFields.filter((field) => field.type !== 'hidden');

    for (const f of visibleFields) {
      if (f.required && isEmptyFieldValue(values[f.field])) {
        setError(`${f.label} is required`);
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {};
      for (const f of activeFields) {
        const val = values[f.field];
        if (val == null || isEmptyFieldValue(val)) continue;
        if (f.type === 'multiselect') {
          payload[f.apiField ?? f.field] = Array.isArray(val) ? val : [val];
        } else {
          const raw = Array.isArray(val) ? val[0] ?? '' : val;
          const coerced = coerceFieldValue(f, raw);
          if (coerced !== undefined) payload[f.apiField ?? f.field] = coerced;
        }
      }

      const apiPath = resolveOptionsEndpointTemplate(def.api.path, values);
      if (!apiPath) {
        setError('Please complete the required reference fields first');
        return;
      }

      if (def.api.method === 'POST') {
        await api.post(apiPath, payload);
      } else {
        await api.put(apiPath, payload);
      }

      setSuccess('Created successfully');
      setTimeout(() => {
        onClose();
        if (def.refetch) onSuccess();
      }, 800);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent dock="right" className="flex max-h-[90vh] w-[min(100vw-32px,1000px)] max-w-none flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>{def.title}</DialogTitle>
          <DialogDescription>{def.description ?? 'Complete the fields and submit to create the record.'}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded p-2">
              <CheckCircle2 className="h-4 w-4 shrink-0" /> {success}
            </div>
          )}

          <div className={getDialogGridClass(def.layoutColumns ?? 6)}>
            {def.fields.filter((f) => f.type !== 'hidden' && isFieldVisible(f, values)).map(f => (
              <div key={f.field} className={getFieldSpanClass(f, def.layoutColumns ?? 6)}>
                <Label className="text-xs font-medium">
                  {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                </Label>
                {f.type === 'select' ? (
                  <Select
                    value={getSingleFieldValue(values[f.field])}
                    onValueChange={v => setValues(p => ({ ...p, [f.field]: v }))}
                  >
                    <SelectTrigger className="h-8 mt-1 text-sm"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                    <SelectContent>
                      {(f.options ?? dynamicOptions[f.field] ?? []).map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === 'multiselect' ? (
                  <MultiSelectField
                    field={f}
                    options={f.options ?? dynamicOptions[f.field] ?? []}
                    value={getMultiFieldValue(values[f.field])}
                    onChange={(next) => setValues((prev) => ({ ...prev, [f.field]: next }))}
                    loadOptions={f.optionsEndpoint
                      ? async (search) => {
                          const endpoint = resolveOptionsEndpointTemplate(f.optionsEndpoint!, values);
                          if (!endpoint) return [];
                          const map = f.optionsMap ?? { label: 'name', value: 'id' };
                          return fetchOptionItems(endpoint, map, { search, limit: 50, page: 1 });
                        }
                      : undefined}
                  />
                ) : f.type === 'textarea' ? (
                  <Textarea
                    className="mt-1 text-sm"
                    rows={2}
                    placeholder={f.placeholder}
                    value={getMultiFieldValue(values[f.field]).length > 0 ? getMultiFieldValue(values[f.field]).join(', ') : getSingleFieldValue(values[f.field])}
                    onChange={e => setValues(p => ({ ...p, [f.field]: e.target.value }))}
                  />
                ) : (
                  <Input
                    className="h-8 mt-1 text-sm"
                    type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                    placeholder={f.placeholder}
                    value={getSingleFieldValue(values[f.field])}
                    onChange={e => setValues(p => ({ ...p, [f.field]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {def.submitLabel ?? 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function buildUpdatePayload(
  fields: FormFieldDef[],
  values: Record<string, string>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = values[field.field] ?? '';
    if (raw === '') continue;
    const coerced = coerceFieldValue(field, raw);
    if (coerced !== undefined) payload[field.apiField ?? field.field] = coerced;
  }
  return payload;
}

async function submitTabUpdate(
  tab: TabDef,
  rowId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  if (!tab.updateApi) throw new Error(`Tab ${tab.key} has no updateApi configured`);
  const path = tab.updateApi.path.replace(':id', rowId);
  if (tab.updateApi.method === 'PATCH') {
    await api.patch(path, payload);
    return;
  }
  await api.put(path, payload);
}

function RecordEditDialog({
  tab,
  row,
  open,
  onClose,
  onSuccess,
}: {
  tab: TabDef;
  row: Record<string, unknown> | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const fields = tab.editFields ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !row) return;
    const initial: Record<string, string> = {};
    fields.forEach((field) => {
      initial[field.field] = serializeFieldValue(row[field.field], field);
    });
    setValues(initial);
    setError('');
    loadFieldOptions(fields).then(setDynamicOptions).catch(() => setDynamicOptions({}));
  }, [open, row, fields]);

  const handleSubmit = async () => {
    if (!row?.id) return;
    for (const field of fields) {
      if (field.required && !(values[field.field] ?? '').trim()) {
        setError(`${field.label} is required`);
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      await submitTabUpdate(tab, String(row.id), buildUpdatePayload(fields, values));
      onClose();
      onSuccess();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent dock="right" className="flex max-h-[90vh] w-[min(100vw-32px,1000px)] max-w-none flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>{tab.detailTitle ?? `${tab.label} Editor`}</DialogTitle>
          <DialogDescription>Update the selected record in the current working context.</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {error && (
            <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <div className={getDialogGridClass(6)}>
            {fields.map((field) => {
              const options = buildOptions(field, dynamicOptions);
              return (
                <div key={field.field} className={getFieldSpanClass(field, 6)}>
                  <Label className="text-xs font-medium">
                    {field.label}
                    {field.required && <span className="ml-0.5 text-red-500">*</span>}
                  </Label>
                  {field.type === 'select' ? (
                    <Select
                      value={values[field.field] ?? ''}
                      onValueChange={(value) => setValues((prev) => ({ ...prev, [field.field]: value }))}
                    >
                      <SelectTrigger className="mt-1 h-8 text-sm">
                        <SelectValue placeholder={`Select ${field.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : field.type === 'textarea' ? (
                    <Textarea
                      className="mt-1 text-sm"
                      rows={3}
                      value={values[field.field] ?? ''}
                      onChange={(event) => setValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                    />
                  ) : (
                    <Input
                      className="mt-1 h-8 text-sm"
                      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                      value={values[field.field] ?? ''}
                      onChange={(event) => setValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {tab.attachmentReferenceType && row?.id ? (
            <AttachmentSection
              referenceType={tab.attachmentReferenceType}
              referenceId={String(row.id)}
              title={tab.attachmentTitle ?? 'Attachments'}
            />
          ) : null}
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentSection({
  referenceType,
  referenceId,
  title,
}: {
  referenceType: string;
  referenceId: string;
  title: string;
}) {
  const [attachments, setAttachments] = useState<Array<Record<string, unknown>>>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const fetchAttachments = useCallback(async () => {
    setAttachmentsLoading(true);
    setAttachmentsError('');
    try {
      const res = await api.get<{ items?: Array<Record<string, unknown>> }>(
        `/wms/attachments?referenceType=${encodeURIComponent(referenceType)}&referenceId=${encodeURIComponent(referenceId)}`,
      );
      setAttachments(res?.items ?? []);
    } catch (err: any) {
      setAttachmentsError(err?.message ?? 'Failed to load attachments');
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [referenceId, referenceType]);

  const handleUploadAttachment = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const startedAt = nowMs();
    const fileCount = files.length;
    setUploadingAttachment(true);
    setAttachmentsError('');
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('referenceType', referenceType);
        formData.append('referenceId', referenceId);
        const token = getToken();
        const response = await fetch(`${BASE}/wms/attachments`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        if (!response.ok) {
          throw new Error(`Upload failed (${response.status})`);
        }
      }
      await fetchAttachments();
      await recordWmsRendererActionOutcome({
        actionId: 'wms-attachment-upload',
        actionType: 'upload',
        outcome: 'succeeded',
        entityType: referenceType,
        entityId: referenceId,
        summary: `Uploaded ${fileCount} WMS attachment${fileCount === 1 ? '' : 's'}.`,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          referenceType,
          fileCount,
        },
      });
    } catch (err: any) {
      const message = getErrorMessage(err, 'Failed to upload attachment');
      await recordWmsRendererActionOutcome({
        actionId: 'wms-attachment-upload',
        actionType: 'upload',
        outcome: 'failed',
        entityType: referenceType,
        entityId: referenceId,
        summary: 'WMS attachment upload failed.',
        errorCode: 'WMS_ATTACHMENT_UPLOAD_FAILED',
        errorMessage: message,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          referenceType,
          fileCount,
        },
      });
      setAttachmentsError(message);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleOpenAttachment = async (attachment: Record<string, unknown>) => {
    const startedAt = nowMs();
    const attachmentId = String(attachment.id ?? '');
    const popup = window.open('', '_blank', 'popup=yes,width=1280,height=900');
    if (!popup) {
      setAttachmentsError('Popup blocked. Please allow popups for attachments.');
      await recordWmsRendererActionOutcome({
        actionId: 'wms-attachment-open',
        actionType: 'download',
        outcome: 'blocked',
        entityType: referenceType,
        entityId: referenceId,
        summary: 'WMS attachment open was blocked by the browser popup policy.',
        errorCode: 'POPUP_BLOCKED',
        errorMessage: 'Popup blocked. Please allow popups for attachments.',
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          referenceType,
          attachmentId,
          filename: attachment.filename ?? null,
        },
      });
      return;
    }

    popup.document.title = String(attachment.filename ?? 'Attachment');
    popup.document.body.innerHTML = '<div style="font-family: sans-serif; padding: 16px;">Loading attachment...</div>';

    try {
      const token = getToken();
      const response = await fetch(`${BASE}/wms/attachments/${encodeURIComponent(attachmentId)}/content`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Open failed (${response.status})`);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      popup.location.replace(objectUrl);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      await recordWmsRendererActionOutcome({
        actionId: 'wms-attachment-open',
        actionType: 'download',
        outcome: 'succeeded',
        entityType: referenceType,
        entityId: referenceId,
        summary: 'Opened WMS attachment content.',
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          referenceType,
          attachmentId,
          filename: attachment.filename ?? null,
          bytes: blob.size,
        },
      });
    } catch (err: any) {
      popup.close();
      const message = getErrorMessage(err, 'Failed to open attachment');
      await recordWmsRendererActionOutcome({
        actionId: 'wms-attachment-open',
        actionType: 'download',
        outcome: 'failed',
        entityType: referenceType,
        entityId: referenceId,
        summary: 'WMS attachment open failed.',
        errorCode: 'WMS_ATTACHMENT_OPEN_FAILED',
        errorMessage: message,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          referenceType,
          attachmentId,
          filename: attachment.filename ?? null,
        },
      });
      setAttachmentsError(message);
    }
  };

  useEffect(() => {
    void fetchAttachments();
  }, [fetchAttachments]);

  return (
    <div className="mt-4 space-y-3 rounded border border-dashed p-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold">{title}</h4>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => void fetchAttachments()} disabled={attachmentsLoading}>
            <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${attachmentsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted">
            <Upload className="h-3.5 w-3.5" />
            {uploadingAttachment ? 'Uploading...' : 'Add Attachment'}
            <Input
              type="file"
              multiple
              className="hidden"
              disabled={uploadingAttachment}
              onChange={(event) => {
                void handleUploadAttachment(event.target.files);
                event.currentTarget.value = '';
              }}
            />
          </Label>
        </div>
      </div>

      {attachmentsError && (
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4 shrink-0" /> {attachmentsError}
        </div>
      )}

      {attachmentsLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading attachments...
        </div>
      ) : attachments.length === 0 ? (
        <div className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
          No attachments yet
        </div>
      ) : (
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-2 py-1 text-left">File</th>
                <th className="px-2 py-1 text-left">Type</th>
                <th className="px-2 py-1 text-left">Size</th>
                <th className="px-2 py-1 text-left">Created</th>
                <th className="px-2 py-1 text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((attachment) => (
                <tr key={String(attachment.id)} className="border-t">
                  <td className="px-2 py-1 font-medium">{formatValue(attachment.filename, undefined)}</td>
                  <td className="px-2 py-1">{formatValue(attachment.mime_type, undefined)}</td>
                  <td className="px-2 py-1">{formatValue(attachment.size_bytes, 'number')}</td>
                  <td className="px-2 py-1">{formatValue(attachment.created_at, 'datetime')}</td>
                  <td className="px-2 py-1 text-right">
                    <Button size="sm" variant="outline" onClick={() => void handleOpenAttachment(attachment)}>
                      <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DetailTableSection({
  title,
  rows,
}: {
  title: string;
  rows: Array<Record<string, unknown>>;
}) {
  if (rows.length === 0) return null;
  const columns = Object.keys(rows[0] ?? {}).slice(0, 8);
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">{title}</h4>
      <div className="overflow-x-auto rounded border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-2 py-1 text-left font-medium text-muted-foreground">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className="border-t">
                {columns.map((column) => (
                  <td key={column} className="px-2 py-1 align-top">
                    {typeof row[column] === 'object' && row[column] !== null
                      ? JSON.stringify(row[column])
                      : formatValue(row[column], undefined)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailContentSections({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const scalarEntries = Object.entries(data).filter(([key, value]) => !shouldHideScalarField(key, value) && !Array.isArray(value) && (typeof value !== 'object' || value === null));
  const objectEntries = Object.entries(data).filter(([, value]) => !Array.isArray(value) && typeof value === 'object' && value !== null);
  const arrayEntries = Object.entries(data).filter(([, value]) => Array.isArray(value)) as Array<[string, Array<Record<string, unknown>>]>;

  return (
    <>
      {scalarEntries.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {scalarEntries.map(([key, value]) => (
            <div key={key} className="rounded border p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{key}</p>
              <p className="mt-1 text-sm">{formatValue(value, undefined)}</p>
            </div>
          ))}
        </div>
      )}

      {objectEntries.map(([key, value]) => (
        <div key={key} className="space-y-2">
          <h4 className="text-sm font-semibold">{key}</h4>
          <pre className="overflow-x-auto rounded border bg-muted/30 p-3 text-xs">{JSON.stringify(value, null, 2)}</pre>
        </div>
      ))}

      {arrayEntries.map(([key, value]) => (
        <DetailTableSection key={key} title={key} rows={value} />
      ))}
    </>
  );
}

function formatHeaderLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function shouldHideScalarField(key: string, value: unknown): boolean {
  if (key === 'id' || key === 'metadata' || key === 'deleted_at') return true;
  if (/_id$/i.test(key)) return true;
  if (typeof value === 'string' && UUID_RE.test(value)) return true;
  return false;
}

const HEADER_PRIORITY_PATTERNS: Array<{ pattern: RegExp; score: number }> = [
  { pattern: /(^|_)(no|number|code)$/i, score: 0 },
  { pattern: /status/i, score: 1 },
  { pattern: /(type|mode)/i, score: 2 },
  { pattern: /warehouse/i, score: 3 },
  { pattern: /project/i, score: 4 },
  { pattern: /(supplier|vendor)/i, score: 5 },
  { pattern: /customer/i, score: 6 },
  { pattern: /(requested_by|requester|owner|assigned_to)/i, score: 7 },
  { pattern: /(date|at)$/i, score: 8 },
  { pattern: /(qty|quantity|total|balance|open)/i, score: 9 },
];

function rankHeaderField(key: string): number {
  const match = HEADER_PRIORITY_PATTERNS.find((item) => item.pattern.test(key));
  return match?.score ?? 50;
}

function HeaderSummaryGrid({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const [showMoreHeaderFields, setShowMoreHeaderFields] = useState(false);
  const scalarEntries = Object.entries(data).filter(([key, value]) => {
    if (shouldHideScalarField(key, value)) return false;
    return !Array.isArray(value) && (typeof value !== 'object' || value === null);
  });

  if (scalarEntries.length === 0) return null;

  const sortedEntries = [...scalarEntries].sort(([leftKey], [rightKey]) => {
    const rankDiff = rankHeaderField(leftKey) - rankHeaderField(rightKey);
    if (rankDiff !== 0) return rankDiff;
    return leftKey.localeCompare(rightKey);
  });
  const primaryEntries = sortedEntries.slice(0, 12);
  const hiddenEntries = sortedEntries.slice(12);

  return (
    <div className="space-y-3">
      <div className={getDialogGridClass(6)}>
        {primaryEntries.map(([key, value]) => (
          <div key={key} className="rounded border p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {formatHeaderLabel(key)}
            </p>
            <p className="mt-1 text-sm">{formatValue(value, undefined)}</p>
          </div>
        ))}
      </div>
      {hiddenEntries.length > 0 && (
        <div className="space-y-2">
          <Button size="sm" variant="outline" onClick={() => setShowMoreHeaderFields((prev) => !prev)}>
            {showMoreHeaderFields ? <ChevronUp className="mr-1.5 h-3.5 w-3.5" /> : <ChevronDown className="mr-1.5 h-3.5 w-3.5" />}
            {showMoreHeaderFields ? 'Hide Extra Header Fields' : `More Header Fields (${hiddenEntries.length})`}
          </Button>
          {showMoreHeaderFields && (
            <div className={getDialogGridClass(6)}>
              {hiddenEntries.map(([key, value]) => (
                <div key={key} className="rounded border p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {formatHeaderLabel(key)}
                  </p>
                  <p className="mt-1 text-sm">{formatValue(value, undefined)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EditableRecordDetailContent({
  tab,
  rowId,
  data,
  loading,
  error,
  onRefresh,
  variant,
}: {
  tab: TabDef;
  rowId: string;
  data: Record<string, unknown> | null;
  loading: boolean;
  error: string;
  onRefresh: () => void | Promise<void>;
  variant: 'inline' | 'dialog';
}) {
  const INITIAL_VISIBLE_LINE_LIMIT = 50;
  const [showAddLine, setShowAddLine] = useState(false);
  const [lineValues, setLineValues] = useState<Record<string, string | string[]>>({});
  const [lineError, setLineError] = useState('');
  const [lineSaving, setLineSaving] = useState(false);
  const [lineDynamicOptions, setLineDynamicOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [showHeaderEdit, setShowHeaderEdit] = useState(false);
  const [headerValues, setHeaderValues] = useState<Record<string, string>>({});
  const [headerDynamicOptions, setHeaderDynamicOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [headerError, setHeaderError] = useState('');
  const [headerSaving, setHeaderSaving] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [lineEditValues, setLineEditValues] = useState<Record<string, string>>({});
  const [lineEditError, setLineEditError] = useState('');
  const [lineEditSaving, setLineEditSaving] = useState(false);
  const [copySheetOpen, setCopySheetOpen] = useState(false);
  const [duplicateSaving, setDuplicateSaving] = useState(false);
  const [copyActionError, setCopyActionError] = useState('');
  const [copyActionNotice, setCopyActionNotice] = useState('');
  const [recentCopiedLineIds, setRecentCopiedLineIds] = useState<string[]>([]);
  const [recentCopyContext, setRecentCopyContext] = useState<{ copyResult: DocumentCopyResult; sourceDetail: DocumentCopySourceDocument | null } | null>(null);
  const [visibleLineLimit, setVisibleLineLimit] = useState(INITIAL_VISIBLE_LINE_LIMIT);
  const [cycleCountLineValues, setCycleCountLineValues] = useState<Record<string, { counted_qty: string; variance_reason: string }>>({});
  const [cycleCountSavingId, setCycleCountSavingId] = useState<string | null>(null);
  const headerFields = tab.editFields ?? [];
  const lineFields = tab.lineFields ?? [];
  const lineEditFields = tab.lineEditFields ?? [];
  const hasHeaderEdit = !!tab.updateApi && headerFields.length > 0;
  const hasLineAdd = !!tab.lineAddApi && lineFields.length > 0;
  const hasLineEdit = !!tab.lineUpdateApi && lineEditFields.length > 0;
  const hasLineDelete = !!tab.lineDeleteApi;
  const isCycleCountInlineEdit = tab.key === 'cycleCounts';
  const cycleCountCanDelete = isCycleCountInlineEdit && hasLineDelete && String(data?.status ?? '') === 'PLANNED';
  const showLineActionsColumn = isCycleCountInlineEdit ? cycleCountCanDelete : (hasLineEdit || hasLineAdd || hasLineDelete);
  const hasVisibleLines = Array.isArray(data?.lines);
  const hasMasterDetailLines = hasVisibleLines || hasLineAdd || hasLineEdit || hasLineDelete;
  const inlineLinesOnly = variant === 'inline';
  const copyConfig = useMemo(() => getWmsDocumentCopyConfig(tab), [tab]);
  const allLineRows = Array.isArray(data?.lines) ? data.lines as Array<Record<string, unknown>> : [];
  const visibleLineRows = allLineRows.slice(0, visibleLineLimit);
  const hiddenLineCount = Math.max(allLineRows.length - visibleLineRows.length, 0);
  const extraDetailData = Object.fromEntries(
    Object.entries(data ?? {}).filter(([key, value]) => key !== 'lines' && (Array.isArray(value) || (typeof value === 'object' && value !== null))),
  );
  const hasExtraDetailData = Object.keys(extraDetailData).length > 0;
  const copiedVarianceCount = useMemo(() => {
    if (!recentCopyContext) return 0;
    const sourceById = new Map((recentCopyContext.sourceDetail?.lines ?? []).map((line) => [String(line.sourceLineId ?? ''), line]));
    let count = 0;
    for (const row of allLineRows) {
      const rowIdValue = String(row.id ?? '');
      if (!recentCopiedLineIds.includes(rowIdValue)) continue;
      const metadata = parseJsonObject(row.metadata);
      const snapshot = parseJsonObject(metadata.copied_source_snapshot);
      const sourceLine = sourceById.get(String(metadata.copied_from_source_line_id ?? ''));
      const sourceUnitPrice = Number(sourceLine?.unitPrice ?? snapshot.unitPrice ?? 0);
      const sourceAmount = Number(sourceLine?.amount ?? snapshot.amount ?? 0);
      const currentUnitPrice = Number(row.unit_price ?? row.unitPrice ?? 0);
      const currentAmount = Number(row.amount ?? 0);
      if (Math.abs(currentUnitPrice - sourceUnitPrice) > 0.004 || Math.abs(currentAmount - sourceAmount) > 0.004) count += 1;
    }
    return count;
  }, [allLineRows, recentCopiedLineIds, recentCopyContext]);
  const firstCopiedLineComparison = useMemo(() => {
    if (!recentCopyContext || recentCopiedLineIds.length === 0) return null;
    const firstLineId = recentCopiedLineIds[0];
    const row = allLineRows.find((item) => String(item.id ?? '') === firstLineId);
    if (!row) return null;
    const metadata = parseJsonObject(row.metadata);
    const snapshot = parseJsonObject(metadata.copied_source_snapshot);
    const sourceById = new Map((recentCopyContext.sourceDetail?.lines ?? []).map((line) => [String(line.sourceLineId ?? ''), line]));
    const sourceLine = sourceById.get(String(metadata.copied_from_source_line_id ?? ''));
    return {
      sourceDescription: String(sourceLine?.description ?? snapshot.description ?? ''),
      targetDescription: String(row.description ?? row.sku_name ?? row.sku_code ?? ''),
      sourceUnitPrice: Number(sourceLine?.unitPrice ?? snapshot.unitPrice ?? 0),
      targetUnitPrice: Number(row.unit_price ?? row.unitPrice ?? 0),
      sourceAmount: Number(sourceLine?.amount ?? snapshot.amount ?? 0),
      targetAmount: Number(row.amount ?? 0),
      sourceQuantity: Number(sourceLine?.quantity ?? snapshot.quantity ?? 0),
      targetQuantity: Number(row.qty_ordered ?? row.qty_requested ?? row.qty ?? 0),
      warnings: [
        Math.abs(Number(row.qty_ordered ?? row.qty_requested ?? row.qty ?? 0) - Number(sourceLine?.quantity ?? snapshot.quantity ?? 0)) > 0.004
          ? 'Quantity differs from source'
          : null,
        Math.abs(Number(row.unit_price ?? row.unitPrice ?? 0) - Number(sourceLine?.unitPrice ?? snapshot.unitPrice ?? 0)) > 0.004
          ? 'Unit price differs from source'
          : null,
        Math.abs(Number(row.amount ?? 0) - Number(sourceLine?.amount ?? snapshot.amount ?? 0)) > 0.004
          ? 'Line amount differs from source'
          : null,
      ].filter((value): value is string => Boolean(value)),
    };
  }, [allLineRows, recentCopiedLineIds, recentCopyContext]);

  useEffect(() => {
    setVisibleLineLimit(INITIAL_VISIBLE_LINE_LIMIT);
    setEditingLineId(null);
    setLineEditValues({});
    setLineEditError('');
    setCopyActionError('');
    setCopyActionNotice('');
    setRecentCopiedLineIds([]);
    setRecentCopyContext(null);
    setCopySheetOpen(false);
  }, [rowId, data?.id]);

  useEffect(() => {
    if (recentCopiedLineIds.length === 0) return;
    const firstLineId = recentCopiedLineIds[0];
    const timer = window.setTimeout(() => {
      const targetRow = document.querySelector(`[data-wms-line-id="${firstLineId}"]`);
      if (targetRow instanceof HTMLElement) targetRow.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [recentCopiedLineIds, allLineRows.length]);

  useEffect(() => {
    if (!data || !hasHeaderEdit) return;
    const initial: Record<string, string> = {};
    headerFields.forEach((field) => {
      initial[field.field] = serializeFieldValue(data[field.field], field);
    });
    setHeaderValues(initial);
    setHeaderError('');
    void loadFieldOptions(headerFields).then(setHeaderDynamicOptions).catch(() => setHeaderDynamicOptions({}));
  }, [data, hasHeaderEdit, headerFields]);

  useEffect(() => {
    if (!hasLineAdd) return;
    void loadFieldOptions(lineFields, { id: rowId }).then(setLineDynamicOptions).catch(() => setLineDynamicOptions({}));
  }, [hasLineAdd, lineFields, rowId]);

  useEffect(() => {
    if (!hasLineEdit) return;
    void loadFieldOptions(lineEditFields, { id: rowId })
      .then((options) => setLineDynamicOptions((prev) => ({ ...prev, ...options })))
      .catch(() => undefined);
  }, [hasLineEdit, lineEditFields, rowId]);

  useEffect(() => {
    if (!isCycleCountInlineEdit) {
      setCycleCountLineValues({});
      setCycleCountSavingId(null);
      return;
    }
    const nextValues: Record<string, { counted_qty: string; variance_reason: string }> = {};
    allLineRows.forEach((line) => {
      const lineId = String(line.id ?? '');
      if (!lineId) return;
      nextValues[lineId] = {
        counted_qty: line.counted_qty === null || line.counted_qty === undefined ? '' : String(line.counted_qty),
        variance_reason: String(line.variance_reason ?? ''),
      };
    });
    setCycleCountLineValues(nextValues);
    setCycleCountSavingId(null);
  }, [isCycleCountInlineEdit, allLineRows]);

  const cycleCountDirtyLineIds = useMemo(() => {
    if (!isCycleCountInlineEdit) return [] as string[];
    return allLineRows
      .map((line) => {
        const lineId = String(line.id ?? '');
        if (!lineId) return null;
        const draft = cycleCountLineValues[lineId];
        if (!draft) return null;
        const originalCountedQty = line.counted_qty === null || line.counted_qty === undefined ? '' : String(line.counted_qty);
        const originalRemark = String(line.variance_reason ?? '');
        if (draft.counted_qty !== originalCountedQty || draft.variance_reason !== originalRemark) return lineId;
        return null;
      })
      .filter((value): value is string => Boolean(value));
  }, [allLineRows, cycleCountLineValues, isCycleCountInlineEdit]);

  const resetLineForm = () => {
    const initial: Record<string, string | string[]> = {};
    lineFields.forEach((field) => {
      if (Array.isArray(field.defaultValue)) {
        initial[field.field] = [...field.defaultValue];
      } else if (field.type === 'multiselect') {
        initial[field.field] = [];
      } else {
        initial[field.field] = field.defaultValue ?? '';
      }
    });
    setLineValues(initial);
    setLineError('');
  };

  const handleOpenAddLine = () => {
    resetLineForm();
    setShowAddLine(true);
  };

  const handleAddLine = async () => {
    if (!tab.lineAddApi) return;
    for (const field of lineFields) {
      if (field.required && isEmptyFieldValue(lineValues[field.field])) {
        setLineError(`${field.label} is required`);
        return;
      }
    }

    setLineSaving(true);
    setLineError('');
    try {
      if (isCycleCountInlineEdit) {
        const binId = getSingleFieldValue(lineValues.bin_id);
        const lotNumber = getSingleFieldValue(lineValues.lot_number);
        const skuIds = getMultiFieldValue(lineValues.sku_ids);
        const binOption = (lineDynamicOptions.bin_id ?? []).find((option) => option.value === binId);
        const skuOptions = lineDynamicOptions.sku_ids ?? lineDynamicOptions.sku_id ?? [];
        const manualLines = skuIds.map((skuId) => {
          const skuOption = skuOptions.find((option) => option.value === skuId);
          return {
            binId,
            binCode: binOption?.label ?? undefined,
            skuId,
            skuCode: skuOption?.label ?? undefined,
            lotNumber: lotNumber || undefined,
          };
        });
        await api.post(tab.lineAddApi.path.replace(':id', rowId), { autoGenerate: false, manualLines });
      } else {
        const payload: Record<string, unknown> = {};
        lineFields.forEach((field) => {
          const value = lineValues[field.field];
          if (value == null || isEmptyFieldValue(value)) return;
          const apiKey = field.apiField ?? field.field;
          if (field.type === 'multiselect') {
            payload[apiKey] = getMultiFieldValue(value);
            return;
          }
          const raw = Array.isArray(value) ? (value[0] ?? '') : value;
          const coerced = coerceFieldValue(field, raw);
          if (coerced !== undefined) payload[apiKey] = coerced;
        });
        await api.post(tab.lineAddApi.path.replace(':id', rowId), payload);
      }
      setShowAddLine(false);
      resetLineForm();
      await onRefresh();
    } catch (err: any) {
      setLineError(err?.message ?? 'Failed to add line');
    } finally {
      setLineSaving(false);
    }
  };

  const handleSaveHeader = async () => {
    if (!tab.updateApi) return;
    for (const field of headerFields) {
      if (field.required && !(headerValues[field.field] ?? '').trim()) {
        setHeaderError(`${field.label} is required`);
        return;
      }
    }

    setHeaderSaving(true);
    setHeaderError('');
    try {
      await submitTabUpdate(tab, rowId, buildUpdatePayload(headerFields, headerValues));
      setShowHeaderEdit(false);
      await onRefresh();
    } catch (err: any) {
      setHeaderError(err?.message ?? 'Failed to save header');
    } finally {
      setHeaderSaving(false);
    }
  };

  const handleStartEditLine = (line: Record<string, unknown>) => {
    const initial: Record<string, string> = {};
    lineEditFields.forEach((field) => {
      initial[field.field] = serializeFieldValue(line[field.field], field);
    });
    setEditingLineId(String(line.id));
    setLineEditValues(initial);
    setLineEditError('');
  };

  const handleSaveLineEdit = async () => {
    if (!editingLineId || !tab.lineUpdateApi) return;
    for (const field of lineEditFields) {
      if (field.required && !(lineEditValues[field.field] ?? '').trim()) {
        setLineEditError(`${field.label} is required`);
        return;
      }
    }

    setLineEditSaving(true);
    setLineEditError('');
    try {
      const payload = buildUpdatePayload(lineEditFields, lineEditValues);
      const path = tab.lineUpdateApi.path
        .replace(':id', rowId)
        .replace(':lineId', editingLineId);
      if (tab.lineUpdateApi.method === 'PATCH') {
        await api.patch(path, payload);
      } else {
        await api.put(path, payload);
      }
      setEditingLineId(null);
      setLineEditValues({});
      await onRefresh();
    } catch (err: any) {
      setLineEditError(err?.message ?? 'Failed to save line');
    } finally {
      setLineEditSaving(false);
    }
  };

  const handleDeleteLine = async (lineId: string) => {
    if (!tab.lineDeleteApi) return;
    if (!window.confirm('Delete this line?')) return;
    const startedAt = nowMs();
    try {
      await api.delete(
        tab.lineDeleteApi.path
          .replace(':id', rowId)
          .replace(':lineId', lineId),
      );
      if (editingLineId === lineId) {
        setEditingLineId(null);
        setLineEditValues({});
      }
      await onRefresh();
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-line-delete`,
        actionType: 'delete',
        outcome: 'succeeded',
        entityType: `${tab.key}:line`,
        entityId: lineId,
        summary: `Deleted WMS ${tab.key} line.`,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          parentId: rowId,
          deletePath: tab.lineDeleteApi.path,
        },
      });
    } catch (err: any) {
      const message = getErrorMessage(err, 'Failed to delete line');
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-line-delete`,
        actionType: 'delete',
        outcome: 'failed',
        entityType: `${tab.key}:line`,
        entityId: lineId,
        summary: `Failed to delete WMS ${tab.key} line.`,
        errorCode: 'WMS_LINE_DELETE_FAILED',
        errorMessage: message,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          parentId: rowId,
          deletePath: tab.lineDeleteApi.path,
        },
      });
      setLineEditError(message);
    }
  };

  const handleSaveCycleCountLine = async (lineId: string) => {
    if (!tab.lineUpdateApi) return;
    const values = cycleCountLineValues[lineId] ?? { counted_qty: '', variance_reason: '' };
    const path = tab.lineUpdateApi.path
      .replace(':id', rowId)
      .replace(':lineId', lineId);
    setCycleCountSavingId(lineId);
    setLineEditError('');
    try {
      const payload: Record<string, unknown> = {
        countedQty: values.counted_qty.trim() === '' ? null : Number(values.counted_qty),
        varianceReason: values.variance_reason.trim() || null,
      };
      if (tab.lineUpdateApi.method === 'PATCH') {
        await api.patch(path, payload);
      } else {
        await api.put(path, payload);
      }
      await onRefresh();
    } catch (err: any) {
      setLineEditError(err?.message ?? 'Failed to save line');
    } finally {
      setCycleCountSavingId(null);
    }
  };

  const handleSaveAllCycleCountLines = async () => {
    if (!tab.lineUpdateApi || cycleCountDirtyLineIds.length === 0) return;
    setCycleCountSavingId('__all__');
    setLineEditError('');
    try {
      await Promise.all(
        cycleCountDirtyLineIds.map(async (lineId) => {
          const values = cycleCountLineValues[lineId] ?? { counted_qty: '', variance_reason: '' };
          const path = tab.lineUpdateApi!.path.replace(':id', rowId).replace(':lineId', lineId);
          const payload: Record<string, unknown> = {
            countedQty: values.counted_qty.trim() === '' ? null : Number(values.counted_qty),
            varianceReason: values.variance_reason.trim() || null,
          };
          if (tab.lineUpdateApi!.method === 'PATCH') {
            await api.patch(path, payload);
          } else {
            await api.put(path, payload);
          }
        }),
      );
      await onRefresh();
    } catch (err: any) {
      setLineEditError(err?.message ?? 'Failed to save lines');
    } finally {
      setCycleCountSavingId(null);
    }
  };

  const handleDuplicateDocument = async () => {
    if (!copyConfig) return;
    setDuplicateSaving(true);
    setCopyActionError('');
    setCopyActionNotice('');
    try {
      const result = await api.post<{ copyResult?: DocumentCopyResult }>(copyConfig.duplicatePath(rowId), {});
      await onRefresh();
      setRecentCopiedLineIds(result.copyResult?.copiedLineIds ?? []);
      setRecentCopyContext(result.copyResult ? { copyResult: result.copyResult, sourceDetail: null } : null);
      setCopyActionNotice(`New ${copyConfig.targetLabel} draft created with all lines copied from the current document.`);
    } catch (err: any) {
      setCopyActionError(err?.message ?? `Failed to duplicate ${copyConfig.targetLabel}`);
    } finally {
      setDuplicateSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
        <AlertCircle className="h-4 w-4 shrink-0" /> {error}
      </div>
    );
  }

  if (!data) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No detail data</p>;
  }

  return (
    <div className={variant === 'dialog' ? 'max-h-[70vh] space-y-4 overflow-y-auto pr-1' : 'space-y-4'}>
      {!inlineLinesOnly && (
        hasMasterDetailLines ? (
          <div className="space-y-3 rounded border border-dashed p-3">
            <h4 className="text-sm font-semibold">Header</h4>
            <HeaderSummaryGrid
              data={Object.fromEntries(
                Object.entries(data).filter(([key, value]) => key !== 'lines' && !Array.isArray(value))
              )}
            />
          </div>
        ) : (
          <DetailContentSections data={data} />
        )
      )}

      {!inlineLinesOnly && hasMasterDetailLines && hasExtraDetailData && (
        <div className="space-y-2">
          <Button size="sm" variant="outline" onClick={() => setShowMoreDetails((prev) => !prev)}>
            {showMoreDetails ? <ChevronUp className="mr-1.5 h-3.5 w-3.5" /> : <ChevronDown className="mr-1.5 h-3.5 w-3.5" />}
            {showMoreDetails ? 'Hide More Details' : 'More Details'}
          </Button>
          {showMoreDetails && <DetailContentSections data={extraDetailData} />}
        </div>
      )}

      {!inlineLinesOnly && hasHeaderEdit && (
        <div className="space-y-3 rounded border border-dashed p-3">
          {!showHeaderEdit ? (
            <Button size="sm" variant="outline" onClick={() => setShowHeaderEdit(true)}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit
            </Button>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold">Edit Header</h4>
              </div>
              {headerError && (
                <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {headerError}
                </div>
              )}
              <div className={getDialogGridClass(6)}>
                {headerFields.map((field) => {
                  const options = buildOptions(field, headerDynamicOptions);
                  return (
                    <div key={field.field} className={getFieldSpanClass(field, 6)}>
                      <Label className="text-xs font-medium">
                        {field.label}
                        {field.required && <span className="ml-0.5 text-red-500">*</span>}
                      </Label>
                      {field.type === 'select' ? (
                        <Select
                          value={headerValues[field.field] ?? ''}
                          onValueChange={(value) => setHeaderValues((prev) => ({ ...prev, [field.field]: value }))}
                        >
                          <SelectTrigger className="mt-1 h-8 text-sm">
                            <SelectValue placeholder={`Select ${field.label}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {options.map((option) => (
                              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field.type === 'textarea' ? (
                        <Textarea
                          className="mt-1 text-sm"
                          rows={3}
                          value={headerValues[field.field] ?? ''}
                          onChange={(event) => setHeaderValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                        />
                      ) : (
                        <Input
                          className="mt-1 h-8 text-sm"
                          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                          value={headerValues[field.field] ?? ''}
                          onChange={(event) => setHeaderValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => void handleSaveHeader()} disabled={headerSaving}>
                  {headerSaving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowHeaderEdit(false)} disabled={headerSaving}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {hasVisibleLines && (
        <div className="space-y-3 rounded border border-dashed p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">Lines</h4>
              {copyActionError ? <div className="text-xs text-red-600">{copyActionError}</div> : null}
              {!copyActionError && copyActionNotice ? <div className="text-xs text-emerald-700">{copyActionNotice}</div> : null}
              {!copyActionError && !copyActionNotice && recentCopiedLineIds.length > 0 ? <div className="text-xs text-emerald-700">{recentCopiedLineIds.length} copied line(s) highlighted below.</div> : null}
              {!copyActionError && recentCopiedLineIds.length > 0 && copiedVarianceCount > 0 ? <div className="text-xs text-amber-700">{copiedVarianceCount} copied line(s) have price or amount variance against the source.</div> : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isCycleCountInlineEdit && (
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => void handleSaveAllCycleCountLines()}
                  disabled={cycleCountSavingId !== null || cycleCountDirtyLineIds.length === 0}
                >
                  {cycleCountSavingId === '__all__' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                  Save All
                  {cycleCountDirtyLineIds.length > 0 ? ` (${cycleCountDirtyLineIds.length})` : ''}
                </Button>
              )}
              {allLineRows.length > INITIAL_VISIBLE_LINE_LIMIT && (
                <span className="text-xs text-muted-foreground">
                  Showing {visibleLineRows.length} of {allLineRows.length}
                </span>
              )}
              {copyConfig ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => void handleDuplicateDocument()}
                    disabled={duplicateSaving}
                  >
                    {duplicateSaving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <BookCopy className="mr-1 h-3 w-3" />}
                    Copy Entire
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => {
                      setCopyActionError('');
                      setCopySheetOpen(true);
                    }}
                  >
                    <BookCopy className="mr-1 h-3 w-3" />
                    Copy Line
                  </Button>
                </>
              ) : null}
            </div>
          </div>

          {lineEditError && (
            <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 shrink-0" /> {lineEditError}
            </div>
          )}

          {firstCopiedLineComparison ? (
            <Card className="border-sky-200 bg-sky-50/50">
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Copied Line Comparison</div>
                    <div className="text-xs text-slate-600">Source line vs current document line. The first copied line is auto-focused so pricing drift is visible immediately.</div>
                  </div>
                  <Badge variant="outline" className="border-sky-200 bg-white text-sky-700">First copied line</Badge>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border bg-white px-3 py-2 text-sm">
                    <div className="text-xs text-muted-foreground">Source Quantity</div>
                    <div className="mt-1 font-semibold">{firstCopiedLineComparison.sourceQuantity}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Current {firstCopiedLineComparison.targetQuantity}</div>
                  </div>
                  <div className="rounded-xl border bg-white px-3 py-2 text-sm">
                    <div className="text-xs text-muted-foreground">Source Price</div>
                    <div className="mt-1 font-semibold">{firstCopiedLineComparison.sourceUnitPrice.toFixed(2)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Current {firstCopiedLineComparison.targetUnitPrice.toFixed(2)}</div>
                  </div>
                  <div className="rounded-xl border bg-white px-3 py-2 text-sm">
                    <div className="text-xs text-muted-foreground">Source Amount</div>
                    <div className="mt-1 font-semibold">{firstCopiedLineComparison.sourceAmount.toFixed(2)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Current {firstCopiedLineComparison.targetAmount.toFixed(2)}</div>
                  </div>
                </div>
                <div className="rounded-xl border bg-white px-3 py-2 text-sm">
                  <div className="text-xs text-muted-foreground">Source Description</div>
                  <div className="mt-1 font-medium">{firstCopiedLineComparison.sourceDescription || '—'}</div>
                  <div className="mt-2 text-xs text-muted-foreground">Current Description</div>
                  <div className="mt-1">{firstCopiedLineComparison.targetDescription || '—'}</div>
                </div>
                {firstCopiedLineComparison.warnings.length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Pricing Variance Alerts</div>
                    <div className="flex flex-wrap gap-2">
                      {firstCopiedLineComparison.warnings.map((warning) => (
                        <Badge key={warning} variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{warning}</Badge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Pricing matches copied source.</Badge>
                )}
              </CardContent>
            </Card>
          ) : null}

          <div className="overflow-x-auto rounded border">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  {isCycleCountInlineEdit ? (
                    <>
                      <th className="px-2 py-1 text-left">Inventory Code</th>
                      <th className="px-2 py-1 text-left">SKU Code</th>
                      <th className="px-2 py-1 text-left">Description</th>
                      <th className="px-2 py-1 text-left">Bin</th>
                      <th className="px-2 py-1 text-left">System Qty</th>
                      <th className="px-2 py-1 text-left">Counted Qty</th>
                      <th className="px-2 py-1 text-left">Remark</th>
                      <th className="px-2 py-1 text-left">Status</th>
                    </>
                  ) : (
                    <>
                      <th className="px-2 py-1 text-left">Line</th>
                      <th className="px-2 py-1 text-left">Type</th>
                      <th className="px-2 py-1 text-left">SKU</th>
                      <th className="px-2 py-1 text-left">Qty</th>
                      <th className="px-2 py-1 text-left">Result</th>
                      <th className="px-2 py-1 text-left">UOM</th>
                      <th className="px-2 py-1 text-left">Status</th>
                    </>
                  )}
                  {showLineActionsColumn && (
                    <th className="px-2 py-1 text-right">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {visibleLineRows.map((line) => {
                  const lineId = String(line.id ?? '');
                  const itemType = String(line.item_type ?? line.product_category ?? line.category ?? 'OTHER');
                  const displayLine = line.line_number ?? line.sequence_no ?? line.seq_no ?? '—';
                  const displayQty = line.qty_ordered ?? line.qty_requested ?? line.qty ?? line.system_qty ?? '—';
                  const displayResult = line.qty_fulfilled ?? line.qty_completed ?? line.qty_received ?? line.counted_qty ?? line.qty_failed ?? line.variance_qty ?? '—';
                  const isEditingLine = editingLineId === String(line.id);
                  const cycleCountDraft = cycleCountLineValues[lineId] ?? {
                    counted_qty: line.counted_qty === null || line.counted_qty === undefined ? '' : String(line.counted_qty),
                    variance_reason: String(line.variance_reason ?? ''),
                  };
                  const editableFieldGroups = isEditingLine
                    ? Array.from({ length: 7 }, (_, index) =>
                        lineEditFields.filter((_, fieldIndex) => fieldIndex % 7 === index),
                      )
                    : [];
                  return (
                    <Fragment key={lineId}>
                      <tr
                        data-wms-line-id={lineId}
                        className={`border-t ${isEditingLine ? 'bg-muted/20 align-top' : ''} ${recentCopiedLineIds.includes(String(line.id)) ? 'bg-emerald-50/80' : ''}`}
                        onDoubleClick={() => {
                          if (hasLineEdit && !isCycleCountInlineEdit) handleStartEditLine(line);
                        }}
                      >
                        {isCycleCountInlineEdit ? (
                          <>
                            <td className="px-2 py-1 align-top">{formatValue(line.inventory_code ?? '-', undefined)}</td>
                            <td className="px-2 py-1 align-top">{formatValue(line.sku_code, undefined)}</td>
                            <td className="px-2 py-1 align-top">{formatValue(line.description ?? line.sku_name ?? '-', undefined)}</td>
                            <td className="px-2 py-1 align-top">
                              <div>{formatValue(line.bin_code, undefined)}</div>
                              {line.lot_number ? <div className="text-muted-foreground">Lot: {formatValue(line.lot_number, undefined)}</div> : null}
                            </td>
                            <td className="px-2 py-1 align-top">{formatValue(line.system_qty, 'number')}</td>
                            <td className="px-2 py-1 align-top">
                              <Input
                                className="h-8 min-w-[110px] text-xs"
                                type="number"
                                min="0"
                                value={cycleCountDraft.counted_qty}
                                onChange={(event) => setCycleCountLineValues((prev) => ({
                                  ...prev,
                                  [lineId]: {
                                    counted_qty: event.target.value,
                                    variance_reason: prev[lineId]?.variance_reason ?? cycleCountDraft.variance_reason,
                                  },
                                }))}
                              />
                            </td>
                            <td className="px-2 py-1 align-top">
                              <Textarea
                                className="min-h-[64px] min-w-[220px] text-xs"
                                rows={3}
                                value={cycleCountDraft.variance_reason}
                                onChange={(event) => setCycleCountLineValues((prev) => ({
                                  ...prev,
                                  [lineId]: {
                                    counted_qty: prev[lineId]?.counted_qty ?? cycleCountDraft.counted_qty,
                                    variance_reason: event.target.value,
                                  },
                                }))}
                              />
                            </td>
                            <td className="px-2 py-1 align-top">{formatValue(line.status, undefined)}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-2 py-1 align-top">
                              {isEditingLine && editableFieldGroups[0]?.length ? (
                                <div className="space-y-2">
                                  {editableFieldGroups[0].map((field) => (
                                    <div key={field.field} className="space-y-1">
                                      <Label className="text-[11px] font-medium">{field.label}</Label>
                                      <Input
                                        className="h-8 text-xs"
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={lineEditValues[field.field] ?? ''}
                                        onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                formatValue(displayLine, 'number')
                              )}
                            </td>
                            <td className="px-2 py-1 align-top">
                              {isEditingLine && editableFieldGroups[1]?.length ? (
                                <div className="space-y-2">
                                  {editableFieldGroups[1].map((field) => {
                                    const options = buildOptions(field, lineDynamicOptions);
                                    return (
                                      <div key={field.field} className="space-y-1">
                                        <Label className="text-[11px] font-medium">{field.label}</Label>
                                        {field.type === 'select' ? (
                                          <Select
                                            value={lineEditValues[field.field] ?? ''}
                                            onValueChange={(value) => setLineEditValues((prev) => ({ ...prev, [field.field]: value }))}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue placeholder={`Select ${field.label}`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {options.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        ) : (
                                          <Input
                                            className="h-8 text-xs"
                                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                            value={lineEditValues[field.field] ?? ''}
                                            onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                itemType
                              )}
                            </td>
                            <td className="px-2 py-1 align-top">
                              {isEditingLine && editableFieldGroups[2]?.length ? (
                                <div className="space-y-2">
                                  {editableFieldGroups[2].map((field) => {
                                    const options = buildOptions(field, lineDynamicOptions);
                                    return (
                                      <div key={field.field} className="space-y-1">
                                        <Label className="text-[11px] font-medium">{field.label}</Label>
                                        {field.type === 'select' ? (
                                          <Select
                                            value={lineEditValues[field.field] ?? ''}
                                            onValueChange={(value) => setLineEditValues((prev) => ({ ...prev, [field.field]: value }))}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue placeholder={`Select ${field.label}`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {options.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        ) : field.type === 'textarea' ? (
                                          <Textarea
                                            className="min-h-[64px] text-xs"
                                            rows={3}
                                            value={lineEditValues[field.field] ?? ''}
                                            onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                          />
                                        ) : (
                                          <Input
                                            className="h-8 text-xs"
                                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                            value={lineEditValues[field.field] ?? ''}
                                            onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <>
                                  <div>{formatValue(line.sku_code, undefined)}</div>
                                  <div className="text-muted-foreground">{formatValue(line.sku_name, undefined)}</div>
                                  {line.country_of_origin ? (
                                    <div className="text-muted-foreground">Origin: {formatValue(line.country_of_origin, undefined)}</div>
                                  ) : null}
                                </>
                              )}
                            </td>
                            <td className="px-2 py-1 align-top">
                              {isEditingLine && editableFieldGroups[3]?.length ? (
                                <div className="space-y-2">
                                  {editableFieldGroups[3].map((field) => (
                                    <div key={field.field} className="space-y-1">
                                      <Label className="text-[11px] font-medium">{field.label}</Label>
                                      <Input
                                        className="h-8 text-xs"
                                        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                        value={lineEditValues[field.field] ?? ''}
                                        onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                formatValue(displayQty, 'number')
                              )}
                            </td>
                            <td className="px-2 py-1 align-top">
                              {isEditingLine && editableFieldGroups[4]?.length ? (
                                <div className="space-y-2">
                                  {editableFieldGroups[4].map((field) => {
                                    const options = buildOptions(field, lineDynamicOptions);
                                    return (
                                      <div key={field.field} className="space-y-1">
                                        <Label className="text-[11px] font-medium">{field.label}</Label>
                                        {field.type === 'select' ? (
                                          <Select
                                            value={lineEditValues[field.field] ?? ''}
                                            onValueChange={(value) => setLineEditValues((prev) => ({ ...prev, [field.field]: value }))}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue placeholder={`Select ${field.label}`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {options.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        ) : field.type === 'textarea' ? (
                                          <Textarea
                                            className="min-h-[64px] text-xs"
                                            rows={3}
                                            value={lineEditValues[field.field] ?? ''}
                                            onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                          />
                                        ) : (
                                          <Input
                                            className="h-8 text-xs"
                                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                            value={lineEditValues[field.field] ?? ''}
                                            onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                formatValue(displayResult, 'number')
                              )}
                            </td>
                            <td className="px-2 py-1 align-top">
                              {isEditingLine && editableFieldGroups[5]?.length ? (
                                <div className="space-y-2">
                                  {editableFieldGroups[5].map((field) => {
                                    const options = buildOptions(field, lineDynamicOptions);
                                    return (
                                      <div key={field.field} className="space-y-1">
                                        <Label className="text-[11px] font-medium">{field.label}</Label>
                                        {field.type === 'select' ? (
                                          <Select
                                            value={lineEditValues[field.field] ?? ''}
                                            onValueChange={(value) => setLineEditValues((prev) => ({ ...prev, [field.field]: value }))}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue placeholder={`Select ${field.label}`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {options.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        ) : (
                                          <Input
                                            className="h-8 text-xs"
                                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                            value={lineEditValues[field.field] ?? ''}
                                            onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                formatValue(line.uom, undefined)
                              )}
                            </td>
                            <td className="px-2 py-1 align-top">
                              {isEditingLine && editableFieldGroups[6]?.length ? (
                                <div className="space-y-2">
                                  {editableFieldGroups[6].map((field) => {
                                    const options = buildOptions(field, lineDynamicOptions);
                                    return (
                                      <div key={field.field} className="space-y-1">
                                        <Label className="text-[11px] font-medium">{field.label}</Label>
                                        {field.type === 'select' ? (
                                          <Select
                                            value={lineEditValues[field.field] ?? ''}
                                            onValueChange={(value) => setLineEditValues((prev) => ({ ...prev, [field.field]: value }))}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue placeholder={`Select ${field.label}`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {options.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        ) : field.type === 'textarea' ? (
                                          <Textarea
                                            className="min-h-[64px] text-xs"
                                            rows={3}
                                            value={lineEditValues[field.field] ?? ''}
                                            onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                          />
                                        ) : (
                                          <Input
                                            className="h-8 text-xs"
                                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                            value={lineEditValues[field.field] ?? ''}
                                            onChange={(event) => setLineEditValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                formatValue(line.status, undefined)
                              )}
                            </td>
                          </>
                        )}
                        {showLineActionsColumn && (
                          <td className="px-2 py-1 align-top">
                            <div className="flex items-start justify-end gap-1">
                              {isCycleCountInlineEdit ? (
                                <>
                                  {cycleCountCanDelete && (
                                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => void handleDeleteLine(lineId)} disabled={cycleCountSavingId === lineId}>
                                      <Trash2 className="mr-1 h-3 w-3" />
                                      Delete
                                    </Button>
                                  )}
                                </>
                              ) : isEditingLine ? (
                                <>
                                  <Button size="sm" className="h-7 text-xs" onClick={() => void handleSaveLineEdit()} disabled={lineEditSaving}>
                                    {lineEditSaving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                    Save
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingLineId(null)} disabled={lineEditSaving}>
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  {hasLineEdit && (
                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStartEditLine(line)}>
                                      <Pencil className="mr-1 h-3 w-3" />
                                      Edit
                                    </Button>
                                  )}
                                  {hasLineDelete && (
                                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => void handleDeleteLine(String(line.id))}>
                                      <Trash2 className="mr-1 h-3 w-3" />
                                      Delete
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    </Fragment>
                  );
                })}
                {hiddenLineCount > 0 && (
                  <tr className="border-t bg-muted/20">
                    <td colSpan={isCycleCountInlineEdit ? (showLineActionsColumn ? 9 : 8) : ((showLineActionsColumn ? 8 : 7))} className="px-2 py-3 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setVisibleLineLimit((current) => current + INITIAL_VISIBLE_LINE_LIMIT)}
                      >
                        Load {Math.min(INITIAL_VISIBLE_LINE_LIMIT, hiddenLineCount)} more lines
                      </Button>
                    </td>
                  </tr>
                )}
                {hasLineAdd && showAddLine && (
                  <tr className="border-t bg-muted/10 align-top">
                    {Array.from({ length: 7 }, (_, index) =>
                      lineFields.filter((_, fieldIndex) => fieldIndex % 7 === index),
                    ).map((fieldGroup, columnIndex) => (
                      <td key={`new-col-${columnIndex}`} className="px-2 py-1 align-top">
                        {fieldGroup.length ? (
                          <div className="space-y-2">
                            {fieldGroup.map((field) => {
                              const options = buildOptions(field, lineDynamicOptions);
                              return (
                                <div key={field.field} className="space-y-1">
                                  <Label className="text-[11px] font-medium">
                                    {field.label}
                                    {field.required && <span className="ml-0.5 text-red-500">*</span>}
                                  </Label>
                                  {field.type === 'select' ? (
                                    <Select
                                      value={getSingleFieldValue(lineValues[field.field])}
                                      onValueChange={(value) => setLineValues((prev) => ({ ...prev, [field.field]: value }))}
                                    >
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder={`Select ${field.label}`} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {options.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : field.type === 'multiselect' ? (
                                    <MultiSelectField
                                      field={field}
                                      options={options}
                                      value={getMultiFieldValue(lineValues[field.field])}
                                      onChange={(next) => setLineValues((prev) => ({ ...prev, [field.field]: next }))}
                                    />
                                  ) : field.type === 'textarea' ? (
                                    <Textarea
                                      className="min-h-[64px] text-xs"
                                      rows={3}
                                      value={getSingleFieldValue(lineValues[field.field])}
                                      onChange={(event) => setLineValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                    />
                                  ) : (
                                    <Input
                                      className="h-8 text-xs"
                                      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                      placeholder={field.placeholder}
                                      value={getSingleFieldValue(lineValues[field.field])}
                                      onChange={(event) => setLineValues((prev) => ({ ...prev, [field.field]: event.target.value }))}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </td>
                    ))}
                    <td className="px-2 py-1 align-top">
                      <div className="flex items-start justify-end gap-1">
                        <Button size="sm" onClick={() => void handleAddLine()} disabled={lineSaving}>
                          {lineSaving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowAddLine(false)} disabled={lineSaving}>
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasLineAdd && (
        <div className="space-y-3 rounded border border-dashed p-3">
          {!showAddLine ? (
            <Button size="sm" variant="outline" onClick={handleOpenAddLine}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Row
            </Button>
          ) : (
            <div className="text-xs text-muted-foreground">
              New row is now editing inline inside the line table above.
            </div>
          )}
        </div>
      )}

      {!inlineLinesOnly && tab.attachmentReferenceType ? (
        <AttachmentSection
          referenceType={tab.attachmentReferenceType}
          referenceId={rowId}
          title={tab.attachmentTitle ?? 'Attachments'}
        />
      ) : null}

      {copyConfig ? (
        <DocumentCopySheet
          open={copySheetOpen}
          onOpenChange={setCopySheetOpen}
          title={`Copy Lines Into ${copyConfig.targetLabel}`}
          description="Pick quotation, order, or invoice history, tick the lines you want, then copy them into the current draft document."
          targetId={rowId}
          targetLabel={copyConfig.targetLabel}
          endpoints={copyConfig.endpoints}
          onCopied={async (copyResult, sourceDetail) => {
            await onRefresh();
            setRecentCopiedLineIds(copyResult.copiedLineIds);
            setRecentCopyContext({ copyResult, sourceDetail });
            setCopyActionNotice(`${copyResult.copiedLineCount} line(s) copied into the current draft document.`);
          }}
        />
      ) : null}
    </div>
  );
}

function InlineRecordDetail({
  tab,
  rowId,
}: {
  tab: TabDef;
  rowId: string;
}) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDetail = useCallback(async () => {
    if (!tab.detailApiPath) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get<any>(tab.detailApiPath.replace(':id', rowId));
      const payload = (res?.data ?? res) as Record<string, unknown>;
      setData(payload);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load detail');
    } finally {
      setLoading(false);
    }
  }, [rowId, tab.detailApiPath]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  return (
    <div className="rounded-md border bg-muted/10 p-4">
      <EditableRecordDetailContent
        tab={tab}
        rowId={rowId}
        data={data}
        loading={loading}
        error={error}
        onRefresh={() => void fetchDetail()}
        variant="inline"
      />
    </div>
  );
}

function getRecordNo(data: Record<string, unknown> | null): string {
  if (!data) return '-';
  return String(
    data.asn_number
      ?? data.order_number
      ?? data.qc_number
      ?? data.inventory_code
      ?? data.inventory_id
      ?? data.request_number
      ?? data.id
      ?? '-',
  );
}

function getRecordType(data: Record<string, unknown> | null, tab: TabDef): string {
  if (!data) return tab.label;
  return String(data.inbound_mode ?? data.order_type ?? data.category ?? data.type ?? tab.label);
}

function getRecordStatus(data: Record<string, unknown> | null): string {
  return String(data?.status ?? data?.inspection_status ?? 'DRAFT');
}

function getDetailLines(data: Record<string, unknown> | null): Record<string, unknown>[] {
  if (!data) return [];
  for (const key of ['lines', 'items', 'arrivalUnits', 'attachments']) {
    const value = data[key];
    if (Array.isArray(value)) return value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
  }
  return [];
}

function getScalarHeaderFields(data: Record<string, unknown> | null): Array<[string, unknown]> {
  if (!data) return [];
  const skip = new Set(['id', 'tenant_id', 'metadata', 'lines', 'items', 'arrivalUnits', 'attachments', 'events', 'eventLog', 'created_at', 'updated_at', 'deleted_at']);
  return Object.entries(data)
    .filter(([key, value]) => !skip.has(key) && value != null && typeof value !== 'object')
    .slice(0, 18);
}

function WpSimpleRecordDetailContent({
  tab,
  rowId,
  data,
  loading,
  error,
}: {
  tab: TabDef;
  rowId: string;
  data: Record<string, unknown> | null;
  loading: boolean;
  error: string;
}) {
  if (loading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading detail...
      </div>
    );
  }
  if (error) {
    return <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>;
  }

  const recordNo = getRecordNo(data);
  const recordType = getRecordType(data, tab);
  const status = getRecordStatus(data);
  const headerFields = getScalarHeaderFields(data);
  const lines = getDetailLines(data);
  const lineColumns = Array.from(new Set(lines.flatMap((line) => Object.keys(line))))
    .filter((key) => !['id', 'tenant_id', 'metadata', 'deleted_at', 'updated_at'].includes(key))
    .slice(0, 10);
  const milestones = ['DRAFT', 'SUBMITTED', 'RECEIVING', 'QC_PENDING', 'PUTAWAY_PENDING', 'COMPLETED', 'RECEIVED', 'SHIPPED'];
  const activeIndex = Math.max(0, milestones.findIndex((item) => item === status));

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-3">
        <div>
          <div className="text-xs font-medium uppercase text-muted-foreground">{recordType}</div>
          <div className="text-xl font-semibold">{recordNo}</div>
        </div>
        <div className="flex flex-wrap items-center gap-1 text-xs">
          {milestones.slice(0, 6).map((milestone, index) => (
            <span
              key={milestone}
              className={`rounded-full border px-2 py-1 ${index <= activeIndex ? 'border-green-200 bg-green-50 text-green-700' : 'border-slate-200 bg-white text-slate-500'}`}
            >
              {milestone.replaceAll('_', ' ')}
            </span>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {headerFields.map(([key, value]) => (
          <div key={key} className="space-y-1">
            <Label className="text-[11px] uppercase text-muted-foreground">{key.replaceAll('_', ' ')}</Label>
            <Input className="h-9 bg-background text-sm" value={formatValue(value)} readOnly />
          </div>
        ))}
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="attachments">Attachment</TabsTrigger>
          <TabsTrigger value="events">Event Log</TabsTrigger>
        </TabsList>
        <TabsContent value="items" className="mt-3">
          {lines.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">No items</div>
          ) : (
            <div className="max-h-[48vh] overflow-auto rounded-md border">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    {lineColumns.map((column) => (
                      <th key={column} className="whitespace-nowrap border-b px-2 py-2 text-left text-xs font-medium text-muted-foreground">
                        {column.replaceAll('_', ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={String(line.id ?? index)} className="border-b last:border-b-0">
                      {lineColumns.map((column) => (
                        <td key={column} className="max-w-[240px] px-2 py-2 align-top">
                          {formatValue(line[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="attachments" className="mt-3">
          {tab.attachmentReferenceType ? (
            <AttachmentSection
              referenceType={tab.attachmentReferenceType}
              referenceId={rowId}
              title={tab.attachmentTitle ?? 'Attachments'}
            />
          ) : (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">No attachments</div>
          )}
        </TabsContent>
        <TabsContent value="events" className="mt-3">
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">No event log entries</div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RecordDetailDialog({
  tab,
  rowId,
  open,
  onClose,
}: {
  tab: TabDef;
  rowId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDetail = useCallback(async () => {
    if (!rowId || !tab.detailApiPath) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get<any>(tab.detailApiPath!.replace(':id', rowId));
      const payload = (res?.data ?? res) as Record<string, unknown>;
      setData(payload);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load detail');
    } finally {
      setLoading(false);
    }
  }, [rowId, tab.detailApiPath]);

  useEffect(() => {
    if (!open || !rowId || !tab.detailApiPath) return;
    void fetchDetail();
  }, [open, rowId, tab.detailApiPath, fetchDetail]);

  const isWpDetailSheet = typeof window !== 'undefined' && window.location.pathname.startsWith('/wp');
  const usesEditableWpDetail = isWpDetailSheet && tab.key === 'cycleCounts';
  const usesCustomPanel = tab.detailPanel === 'purchaseOrder'
    || tab.detailPanel === 'materialRequest'
    || tab.detailPanel === 'issueOrder'
    || tab.detailPanel === 'returnForm'
    || tab.detailPanel === 'projectReturn'
    || tab.detailPanel === 'inboundOrder'
    || tab.detailPanel === 'receivingStickers'
    || tab.detailPanel === 'qcRecord'
    || tab.detailPanel === 'qcGroup'
    || tab.detailPanel === 'cycleCount';

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent dock="right" className={usesCustomPanel
        ? 'flex max-h-[90vh] w-[min(100vw-32px,1320px)] max-w-none flex-col overflow-hidden p-0'
        : 'flex max-h-[90vh] w-[min(100vw-32px,1000px)] max-w-none flex-col overflow-hidden p-0'}
      >
        {usesCustomPanel ? (
          <>
            <DialogTitle className="sr-only">{tab.detailTitle ?? `${tab.label} Detail`}</DialogTitle>
            <DialogDescription className="sr-only">Selected warehouse document detail.</DialogDescription>
          </>
        ) : null}
        {!usesCustomPanel ? (
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>{tab.detailTitle ?? `${tab.label} Detail`}</DialogTitle>
            <DialogDescription>{isWpDetailSheet ? 'Items, attachments, and event log for the selected record.' : 'Master/detail working panel for the selected record.'}</DialogDescription>
          </DialogHeader>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab.detailPanel === 'purchaseOrder' ? (
            <WmsPurchaseOrderDetailPanel id={rowId ?? undefined} embedded onClose={onClose} />
          ) : tab.detailPanel === 'materialRequest' ? (
            <WmsMaterialRequestDetailPanel id={rowId ?? undefined} embedded onClose={onClose} />
          ) : tab.detailPanel === 'issueOrder' ? (
            <WmsIssueOrderDetailPanel id={rowId ?? undefined} embedded onClose={onClose} />
          ) : tab.detailPanel === 'returnForm' ? (
            <WmsIssueOrderDetailPanel id={rowId ?? undefined} embedded onClose={onClose} returnModePreset="SINGLE_MATERIAL" />
          ) : tab.detailPanel === 'projectReturn' ? (
            <WmsIssueOrderDetailPanel id={rowId ?? undefined} embedded onClose={onClose} returnModePreset="PROJECT_CLOSE_BALANCE" />
          ) : tab.detailPanel === 'inboundOrder' ? (
            <WmsInboundOrderDetailPanel id={rowId ?? undefined} embedded onClose={onClose} />
          ) : tab.detailPanel === 'qcRecord' ? (
            <WmsQcRecordDetailPanel id={rowId ?? undefined} embedded onClose={onClose} />
          ) : tab.detailPanel === 'qcGroup' ? (
            <WmsQcInboundDetailPanel id={rowId ?? undefined} embedded onClose={onClose} readOnly={tab.key === 'inspectionList'} />
          ) : tab.detailPanel === 'cycleCount' ? (
            <WmsCycleCountDetailPanel id={rowId ?? undefined} embedded onClose={onClose} />
          ) : tab.detailPanel === 'receivingStickers' ? (
            <WmsReceivingStickerPanel inboundOrderId={rowId ?? ''} />
          ) : usesEditableWpDetail ? (
            <EditableRecordDetailContent
              tab={tab}
              rowId={rowId ?? ''}
              data={data}
              loading={loading}
              error={error}
              onRefresh={() => void fetchDetail()}
              variant="dialog"
            />
          ) : isWpDetailSheet ? (
            <WpSimpleRecordDetailContent
              tab={tab}
              rowId={rowId ?? ''}
              data={data}
              loading={loading}
              error={error}
            />
          ) : (
            <EditableRecordDetailContent
              tab={tab}
              rowId={rowId ?? ''}
              data={data}
              loading={loading}
              error={error}
              onRefresh={() => void fetchDetail()}
              variant="dialog"
            />
          )}
        </div>
        {!usesCustomPanel ? (
          <DialogFooter className="shrink-0 border-t px-6 py-4">
            <Button variant="outline" size="sm" onClick={() => void fetchDetail()} disabled={loading}>
              <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ActionRecordDetailDialog({
  tab,
  panel,
  title,
  path,
  rowId,
  open,
  onClose,
}: {
  tab: TabDef;
  panel?: ActionDef['detailPanel'];
  title: string;
  path: string | null;
  rowId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDetail = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get<any>(path);
      const payload = (res?.data ?? res) as Record<string, unknown>;
      setData(payload);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load detail');
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    if (!open || !path) return;
    void fetchDetail();
  }, [fetchDetail, open, path]);

  const usesCustomPanel = panel === 'purchaseOrder' || panel === 'materialRequest' || panel === 'issueOrder' || panel === 'returnForm' || panel === 'projectReturn' || panel === 'inboundOrder' || panel === 'receivingStickers' || panel === 'qcRecord' || panel === 'qcGroup';

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent dock="right" className="flex max-h-[90vh] w-[min(100vw-32px,1320px)] max-w-none flex-col overflow-hidden p-0">
        {usesCustomPanel ? (
          <>
            <DialogTitle className="sr-only">{title}</DialogTitle>
            <DialogDescription className="sr-only">Selected warehouse document detail.</DialogDescription>
          </>
        ) : null}
        {!usesCustomPanel ? (
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>Current-page detail panel.</DialogDescription>
          </DialogHeader>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {panel === 'purchaseOrder' ? (
            <WmsPurchaseOrderDetailPanel id={rowId} embedded onClose={onClose} />
          ) : panel === 'materialRequest' ? (
            <WmsMaterialRequestDetailPanel id={rowId} embedded onClose={onClose} />
          ) : panel === 'issueOrder' ? (
            <WmsIssueOrderDetailPanel id={rowId} embedded onClose={onClose} />
          ) : panel === 'returnForm' ? (
            <WmsIssueOrderDetailPanel id={rowId} embedded onClose={onClose} returnModePreset="SINGLE_MATERIAL" />
          ) : panel === 'projectReturn' ? (
            <WmsIssueOrderDetailPanel id={rowId} embedded onClose={onClose} returnModePreset="PROJECT_CLOSE_BALANCE" />
          ) : panel === 'inboundOrder' ? (
            <WmsInboundOrderDetailPanel id={rowId} embedded onClose={onClose} />
          ) : panel === 'qcRecord' ? (
            <WmsQcRecordDetailPanel id={rowId} embedded onClose={onClose} />
          ) : panel === 'qcGroup' ? (
            <WmsQcInboundDetailPanel id={rowId} embedded onClose={onClose} readOnly={tab.key === 'inspectionList'} />
          ) : panel === 'receivingStickers' ? (
            <WmsReceivingStickerPanel inboundOrderId={rowId} />
          ) : (
            <>
              <EditableRecordDetailContent
                tab={tab}
                rowId={rowId}
                data={data}
                loading={loading}
                error={error}
                onRefresh={() => void fetchDetail()}
                variant="dialog"
              />
              <DialogFooter className="border-t px-6 py-4">
                <Button variant="outline" size="sm" onClick={() => void fetchDetail()} disabled={loading}>
                  <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
                  Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
              </DialogFooter>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Row Action Button ─────────────────────────────────────────────────────

function RowActionButton({
  action, row, statuses, onRefetch, onOpenCsvViewer, onOpenDetail, iconOnly = false,
}: {
  action: ActionDef;
  row: Record<string, unknown>;
  statuses: StatusDef[];
  onRefetch: () => void;
  onOpenCsvViewer?: (action: ActionDef, row: Record<string, unknown>) => void;
  onOpenDetail?: (action: ActionDef, row: Record<string, unknown>) => void;
  iconOnly?: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const Icon = getIcon(action.icon);
  const navigate = useNavigate();

  const handleClick = async () => {
    if (action.confirm && !window.confirm(action.confirm)) return;
    const startedAt = nowMs();
    const entityId = row.id == null ? undefined : String(row.id);
    const outcomeBase = {
      actionId: `wms-row-${action.key}`,
      actionType: inferWmsActionType({ key: action.key, label: action.label, method: action.api?.method }),
      entityType: 'wms-row',
      entityId,
    } as const;

    if (action.route) {
      navigate(canonicalizeWmRoute(interpolateRowPath(action.route, row)));
      return;
    }

    if (action.detailApiPath || action.detailPanel) {
      onOpenDetail?.(action, row);
      return;
    }

    if (action.openApiPath) {
      if (action.openAs === 'csvViewer') {
        onOpenCsvViewer?.(action, row);
        return;
      }
      const path = interpolateRowPath(action.openApiPath, row);
      if (path.includes('//') || path.endsWith('/')) {
        alert('Source file is not available for this row');
        return;
      }
      setLoading(true);
      try {
        const token = getToken();
        const response = await fetch(`${BASE}${path}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!response.ok) throw new Error(await response.text());
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const shouldDownload = action.key.toLowerCase().includes('download') || action.label.toLowerCase().includes('download');
        if (shouldDownload) {
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = String(row.latest_file_name ?? row.file_name ?? 'source.csv');
          anchor.click();
          URL.revokeObjectURL(url);
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        }
        await recordWmsRendererActionOutcome({
          ...outcomeBase,
          actionType: 'download',
          outcome: 'succeeded',
          summary: `Completed WMS row resource ${shouldDownload ? 'download' : 'open'} for ${action.key}.`,
          durationMs: Math.round(nowMs() - startedAt),
          metadata: {
            actionKey: action.key,
            label: action.label,
            path,
            openAs: action.openAs ?? null,
            downloaded: shouldDownload,
            bytes: blob.size,
          },
        });
      } catch (err: any) {
        const message = getErrorMessage(err, 'Failed to open source file');
        await recordWmsRendererActionOutcome({
          ...outcomeBase,
          actionType: 'download',
          outcome: 'failed',
          summary: `Failed WMS row resource open/download for ${action.key}.`,
          errorCode: 'WMS_ROW_RESOURCE_OPEN_FAILED',
          errorMessage: message,
          durationMs: Math.round(nowMs() - startedAt),
          metadata: {
            actionKey: action.key,
            label: action.label,
            path,
            openAs: action.openAs ?? null,
          },
        });
        alert(message);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!action.api) return;
    setLoading(true);
    try {
      const path = action.api.path.replace(':id', String(row.id));
      if (action.api.method === 'POST') {
        await api.post(path, action.api.body ?? {});
      } else if (action.api.method === 'PATCH') {
        await api.patch(path, action.api.body ?? {});
      } else if (action.api.method === 'PUT') {
        await api.put(path, action.api.body ?? {});
      } else if (action.api.method === 'DELETE') {
        await api.delete(path);
      }
      await recordWmsRendererActionOutcome({
        ...outcomeBase,
        outcome: 'succeeded',
        summary: `Completed WMS row action ${action.key}.`,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          actionKey: action.key,
          label: action.label,
          method: action.api.method,
          path,
          toStatus: action.toStatus ?? null,
        },
      });
      onRefetch();
    } catch (err: any) {
      const message = getErrorMessage(err, 'Action failed');
      await recordWmsRendererActionOutcome({
        ...outcomeBase,
        outcome: 'failed',
        summary: `Failed WMS row action ${action.key}.`,
        errorCode: 'WMS_ROW_ACTION_FAILED',
        errorMessage: message,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          actionKey: action.key,
          label: action.label,
          method: action.api.method,
          path: action.api.path.replace(':id', String(row.id)),
          toStatus: action.toStatus ?? null,
        },
      });
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      size={iconOnly ? 'icon' : 'sm'}
      variant={(action.variant as any) ?? 'outline'}
      className={iconOnly ? 'h-7 w-7' : 'h-7 text-xs'}
      onClick={handleClick}
      disabled={loading}
      title={action.label}
      aria-label={action.label}
    >
      {loading ? <Loader2 className={iconOnly ? 'h-3 w-3 animate-spin' : 'mr-1 h-3 w-3 animate-spin'} /> : Icon && <Icon className={iconOnly ? 'h-3 w-3' : 'mr-1 h-3 w-3'} />}
      {!iconOnly ? action.label : null}
    </Button>
  );
}

type CsvViewerState = {
  open: boolean;
  title: string;
  headers: string[];
  rows: string[][];
  loading: boolean;
  error: string | null;
};

const emptyCsvViewerState: CsvViewerState = {
  open: false,
  title: '',
  headers: [],
  rows: [],
  loading: false,
  error: null,
};

type InventoryBreakdownTarget = {
  skuId?: string;
  skuCode?: string;
  skuName?: string;
  warehouseId?: string;
  projectId?: string;
  customerId?: string;
  vendorId?: string;
};

type InventoryBreakdownRow = {
  id: string;
  line_type: 'INBOUND' | 'OUTBOUND' | 'RESERVED' | string;
  timestamp?: string | null;
  project_code?: string | null;
  po_number?: string | null;
  reference_type?: string | null;
  reference_no?: string | null;
  inventory_code?: string | null;
  location?: string | null;
  qty_mm?: number | string | null;
  uom?: string | null;
};

function InventoryBreakdownSheet({
  target,
  onClose,
}: {
  target: InventoryBreakdownTarget | null;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<InventoryBreakdownRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const totalPages = Math.max(1, Math.ceil(total / Math.max(limit, 1)));

  const fetchBreakdown = useCallback(async (nextPage: number) => {
    if (!target?.skuCode && !target?.skuId) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(nextPage));
      params.set('limit', String(limit));
      if (target.skuId) params.set('skuId', target.skuId);
      if (target.skuCode) params.set('skuCode', target.skuCode);
      if (target.warehouseId) params.set('warehouseId', target.warehouseId);
      if (target.projectId) params.set('projectId', target.projectId);
      if (target.customerId) params.set('customerId', target.customerId);
      if (target.vendorId) params.set('vendorId', target.vendorId);
      const res = await api.get<{ items?: InventoryBreakdownRow[]; total?: number; page?: number; limit?: number }>(
        `/wms/inventory/material-breakdown?${params.toString()}`,
      );
      setRows(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total ?? 0));
      setPage(Number(res?.page ?? nextPage));
      setLimit(Number(res?.limit ?? limit));
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(getErrorMessage(err, 'Failed to load inventory breakdown'));
    } finally {
      setLoading(false);
    }
  }, [limit, target]);

  useEffect(() => {
    if (!target) return;
    setPage(1);
    void fetchBreakdown(1);
  }, [fetchBreakdown, target]);

  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent dock="right" className="flex h-[min(90vh,900px)] w-[min(100vw-32px,1180px)] max-w-none flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>Inventory Breakdown</DialogTitle>
          <DialogDescription>
            {target?.skuCode ?? '-'}{target?.skuName ? ` - ${target.skuName}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Movement rows show inventory code and current location. Reserved rows are request based and show negative length.
            </div>
            <Button size="sm" variant="outline" onClick={() => void fetchBreakdown(page)} disabled={loading}>
              {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="mr-1 h-3.5 w-3.5" />}
              Refresh
            </Button>
          </div>
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          ) : null}
          <div className="min-h-0 flex-1 overflow-auto rounded-md border">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-muted">
                <tr>
                  {['Time', 'Type', 'Project No', 'PO No', 'Reference', 'Inventory Code', 'Location', 'Length', 'UOM'].map((header) => (
                    <th key={header} className="whitespace-nowrap border-b px-3 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="h-32 text-center text-muted-foreground">
                      <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                      Loading breakdown...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="h-32 text-center text-muted-foreground">No breakdown rows found.</td>
                  </tr>
                ) : rows.map((row) => {
                  const lineType = String(row.line_type ?? '').toUpperCase();
                  const isReserved = lineType === 'RESERVED';
                  const typeLabel = lineType === 'INBOUND'
                    ? 'Inbound'
                    : lineType === 'OUTBOUND'
                      ? 'Outbound'
                      : formatStatusText(lineType || '-');
                  const typeClass = isReserved
                    ? 'border-amber-300 bg-amber-100 text-amber-800'
                    : lineType === 'INBOUND'
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : lineType === 'OUTBOUND'
                        ? 'border-red-300 bg-red-50 text-red-700'
                        : 'border-emerald-300 bg-emerald-50 text-emerald-700';
                  return (
                    <tr key={row.id} className={`border-b last:border-b-0 ${isReserved ? 'bg-amber-50/60' : ''}`}>
                      <td className="whitespace-nowrap px-3 py-2">{formatValue(row.timestamp, 'datetime')}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <Badge variant="outline" className={typeClass}>
                          {typeLabel}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">{row.project_code || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2">{row.po_number || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="font-medium">{row.reference_no || '-'}</div>
                        <div className="text-xs text-muted-foreground">{row.reference_type || '-'}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{row.inventory_code || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-2">{row.location || '-'}</td>
                      <td className={`whitespace-nowrap px-3 py-2 text-right font-medium ${isReserved ? 'text-amber-800' : ''}`}>
                        {formatValue(row.qty_mm, 'number')}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">{row.uom || 'MM'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter className="flex-row items-center justify-between sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages} · {total} rows
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={loading || page <= 1} onClick={() => void fetchBreakdown(page - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={loading || page >= totalPages} onClick={() => void fetchBreakdown(page + 1)}>Next</Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Data Table (DSL-driven) ───────────────────────────────────────────────

function DslTable({
  pageKey, tab, columns, rows, statusField, statuses, rowActions, search, emptyMessage, onRefetch, simpleMode = false,
  pagination,
  onPageChange,
  onPageSizeChange,
  requestedOpenRowId,
  onClearRequestedOpenRowId,
}: {
  pageKey: string;
  tab: TabDef;
  columns: ColumnDef[];
  rows: Record<string, unknown>[];
  statusField?: string;
  statuses: StatusDef[];
  rowActions?: ActionDef[];
  search: string;
  emptyMessage?: string;
  onRefetch: () => void;
  simpleMode?: boolean;
  pagination?: { total: number; page: number; limit: number; hasMore?: boolean; server: boolean } | null;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  requestedOpenRowId?: string | null;
  onClearRequestedOpenRowId?: () => void;
}) {
  const renderedTab = useRenderedTab();
  const isRenderedTabActive = renderedTab?.isActive ?? true;
  const visibleCols = columns.filter(c => !c.hidden);
  const editableFields = tab.editFields ?? null;
  const editableFieldsKey = editableFields
    ? editableFields.map((field) => `${field.field}:${field.apiField ?? field.field}`).join('|')
    : '';
  const inlineEnabled = tab.editMode === 'inline' || tab.editMode === 'both';
  const dialogEnabled = tab.editMode === 'dialog' || tab.editMode === 'both';
  const detailDialogEnabled = !!tab.detailApiPath && (!tab.detailMode || tab.detailMode === 'dialog' || tab.detailMode === 'both');
  const detailInlineEnabled = !!tab.detailApiPath && (tab.detailMode === 'inline' || tab.detailMode === 'both');
  const prefersMasterDetailSheet = detailDialogEnabled && (!!tab.lineAddApi || !!tab.lineUpdateApi || !!tab.lineDeleteApi);
  const [sort, setSort] = useState<SortState>({ field: null, direction: 'asc' });
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [cellError, setCellError] = useState('');
  const [dialogRow, setDialogRow] = useState<Record<string, unknown> | null>(null);
  const [detailRowId, setDetailRowId] = useState<string | null>(null);
  const [actionDetail, setActionDetail] = useState<{ path: string | null; title: string; rowId: string; panel?: ActionDef['detailPanel'] } | null>(null);
  const [inventoryBreakdownTarget, setInventoryBreakdownTarget] = useState<InventoryBreakdownTarget | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [csvViewer, setCsvViewer] = useState<CsvViewerState>(emptyCsvViewerState);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchActionLoadingKey, setBatchActionLoadingKey] = useState<string | null>(null);
  const batchDeleteEnabled = !!tab.deleteApi;
  const batchActions = tab.batchActions ?? [];
  const selectionEnabled = batchDeleteEnabled || batchActions.length > 0;
  const isPutawayInlineTable = pageKey === 'inbound' && tab.key === 'putaway';
  const [putawayDrafts, setPutawayDrafts] = useState<Record<string, { to_bin_id: string; qty: string; notes: string }>>({});
  const [putawaySavingRowId, setPutawaySavingRowId] = useState<string | null>(null);
  const [inlineWarning, setInlineWarning] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!editableFields || editableFields.length === 0) {
      return;
    }
    loadFieldOptions(editableFields).then((result) => {
      if (!cancelled) {
        setDynamicOptions((current) => {
          const currentKey = JSON.stringify(current);
          const nextKey = JSON.stringify(result);
          return currentKey === nextKey ? current : result;
        });
      }
    }).catch(() => {
      if (!cancelled) {
        setDynamicOptions((current) => (Object.keys(current).length === 0 ? current : {}));
      }
    });
    return () => { cancelled = true; };
  }, [tab.key, editableFieldsKey]);

  useEffect(() => {
    setDynamicOptions({});
    setEditingCell(null);
    setEditValue('');
    setCellError('');
    setDialogRow(null);
    setDetailRowId(null);
    setActionDetail(null);
    setExpandedRowId(null);
    setSelectedRowIds([]);
    setPutawayDrafts({});
    setInlineWarning('');
  }, [tab.key]);

  useEffect(() => {
    if (isRenderedTabActive) return;
    setDialogRow(null);
    setDetailRowId(null);
    setActionDetail(null);
    setInventoryBreakdownTarget(null);
    setCsvViewer(emptyCsvViewerState);
  }, [isRenderedTabActive]);

  useEffect(() => {
    if (!detailDialogEnabled || !requestedOpenRowId) return;
    setDetailRowId(String(requestedOpenRowId));
  }, [detailDialogEnabled, requestedOpenRowId]);

  useEffect(() => {
    if (!isPutawayInlineTable) return;
    setPutawayDrafts((current) => {
      const next: Record<string, { to_bin_id: string; qty: string; notes: string }> = {};
      rows.forEach((row) => {
        if (row.id == null) return;
        const rowId = String(row.id);
        const previous = current[rowId];
        next[rowId] = {
          to_bin_id: previous?.to_bin_id ?? String(row.to_bin_id ?? ''),
          qty: previous?.qty ?? '0',
          notes: previous?.notes ?? String(row.notes ?? ''),
        };
      });
      const currentKey = JSON.stringify(current);
      const nextKey = JSON.stringify(next);
      return currentKey === nextKey ? current : next;
    });
  }, [isPutawayInlineTable, rows]);

  const handleSort = useCallback((field: string) => {
    setSort(prev => {
      if (prev.field !== field) return { field, direction: 'asc' };
      if (prev.direction === 'asc') return { field, direction: 'desc' };
      return { field: null, direction: 'asc' }; // reset
    });
  }, []);

  const serverPagination = pagination?.server === true;

  // Client-side search filter
  const searched = serverPagination ? rows : (search
    ? rows.filter(row =>
        visibleCols.some(col => {
          const v = row[col.field];
          return v != null && formatValue(v, col.format).toLowerCase().includes(search.toLowerCase());
        })
      )
    : rows);

  // Sort after search
  const filtered = sortRows(searched, sort, columns);
  const pageSizeOptions = [25, 50, 100, 200];
  const [pageSize, setPageSize] = useState(tab.pagination?.pageSize ?? 50);
  const [page, setPage] = useState(1);
  const effectivePageSize = serverPagination ? (pagination?.limit ?? tab.pagination?.pageSize ?? 50) : pageSize;
  const totalRows = serverPagination ? Math.max(pagination?.total ?? filtered.length, filtered.length) : filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / effectivePageSize));
  const currentPage = serverPagination
    ? Math.max(1, Math.min(pagination?.page ?? 1, totalPages))
    : Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * effectivePageSize;
  const pageRows = serverPagination ? filtered : filtered.slice(pageStart, pageStart + effectivePageSize);
  const showPagination = serverPagination || tab.pagination?.mode === 'always' || filtered.length > pageSizeOptions[0]!;

  useEffect(() => {
    if (serverPagination) return;
    setPage(1);
  }, [search, serverPagination, sort.field, sort.direction, rows.length, tab.key]);

  useEffect(() => {
    if (serverPagination) return;
    setPageSize(tab.pagination?.pageSize ?? 50);
    setPage(1);
  }, [serverPagination, tab.key, tab.pagination?.pageSize]);

  useEffect(() => {
    if (serverPagination) return;
    if (page > totalPages) setPage(totalPages);
  }, [page, serverPagination, totalPages]);

  const selectableRows = selectionEnabled ? filtered.filter((row) => row.id != null) : [];
  const selectedRows = selectableRows.filter((row) => selectedRowIds.includes(String(row.id)));
  const allFilteredSelected = selectableRows.length > 0
    && selectableRows.every((row) => selectedRowIds.includes(String(row.id)));
  const hasActions = simpleMode
    ? !!(rowActions && rowActions.length > 0)
    : isPutawayInlineTable || detailDialogEnabled || detailInlineEnabled || dialogEnabled || !!(rowActions && rowActions.length > 0);
  const rowActionStyle = tab.rowActionStyle ?? 'button';
  const actionColumnPosition = tab.actionColumnPosition ?? 'end';
  const freezeActionColumn = tab.freezeActionColumn ?? actionColumnPosition === 'end';
  const actionColumnWidth = detailInlineEnabled || (detailDialogEnabled && (dialogEnabled || rowActions?.length)) ? 320 : 180;
  const disableRowDetailOpen = pageKey === 'inventory' || pageKey === 'stockLedger';
  const tableMinWidth = Math.max(
    760,
    visibleCols.reduce((sum, col) => {
      if (typeof col.width === 'number') return sum + col.width;
      if (typeof col.width === 'string') {
        const parsed = Number.parseFloat(col.width);
        return sum + (Number.isFinite(parsed) ? parsed : 140);
      }
      return sum + (simpleMode ? 132 : 150);
    }, 0)
      + (detailInlineEnabled ? 44 : 0)
      + (selectionEnabled ? 44 : 0)
      + (hasActions ? actionColumnWidth : 0)
  );

  const toggleRowSelection = (rowId: string, checked: boolean) => {
    setSelectedRowIds((current) => {
      if (checked) return current.includes(rowId) ? current : [...current, rowId];
      return current.filter((id) => id !== rowId);
    });
  };

  const toggleAllFilteredRows = (checked: boolean) => {
    if (!checked) {
      setSelectedRowIds((current) => current.filter((id) => !selectableRows.some((row) => String(row.id) === id)));
      return;
    }
    setSelectedRowIds((current) => {
      const next = new Set(current);
      selectableRows.forEach((row) => next.add(String(row.id)));
      return Array.from(next);
    });
  };

  const showInlineWarning = (message: string) => {
    setInlineWarning(message);
  };

  const handleBatchDelete = async () => {
    if (!tab.deleteApi || selectedRows.length === 0) return;
    if (!window.confirm(`Remove ${selectedRows.length} selected row${selectedRows.length === 1 ? '' : 's'}?`)) return;

    const startedAt = nowMs();
    setBatchDeleting(true);
    const succeeded: string[] = [];
    const failed: Array<{ id: string; error: string }> = [];

    for (const row of selectedRows) {
      const rowId = String(row.id);
      try {
        await api.delete(tab.deleteApi.path.replace(':id', rowId));
        succeeded.push(rowId);
      } catch (err) {
        failed.push({ id: rowId, error: getErrorMessage(err, 'Delete failed') });
      }
    }

    try {
      const outcome = failed.length === 0 ? 'succeeded' : succeeded.length > 0 ? 'degraded' : 'failed';
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-batch-delete`,
        actionType: 'batch',
        outcome,
        entityType: tab.key,
        selectedEntityRefs: selectedRows.map((row) => ({ type: tab.key, id: String(row.id) })),
        summary: `Batch delete ${outcome} for ${tab.key}: ${succeeded.length} succeeded, ${failed.length} failed.`,
        errorCode: failed.length > 0 ? 'WMS_BATCH_DELETE_PARTIAL_OR_FAILED' : undefined,
        errorMessage: failed.length > 0 ? failed.map((item) => `${item.id}: ${item.error}`).join('; ') : undefined,
        rollbackHint: 'restore-soft-delete',
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          deletePath: tab.deleteApi.path,
          selectedCount: selectedRows.length,
          succeededIds: succeeded,
          failed,
        },
      });

      if (failed.length === 0) {
        setSelectedRowIds([]);
      } else {
        setSelectedRowIds(failed.map((item) => item.id));
        showInlineWarning(`${failed.length} row${failed.length === 1 ? '' : 's'} failed to delete. Successful rows were removed.`);
      }
      await onRefetch();
    } finally {
      setBatchDeleting(false);
    }
  };

  const handleBatchAction = async (action: BatchActionDef) => {
    if (selectedRows.length === 0) return;
    const confirmationMessage = action.confirm ?? `${action.label} ${selectedRows.length} selected row${selectedRows.length === 1 ? '' : 's'}?`;
    if (!window.confirm(confirmationMessage)) return;

    const startedAt = nowMs();
    setBatchActionLoadingKey(action.key);
    const succeeded: string[] = [];
    const failed: Array<{ id: string; error: string }> = [];

    for (const row of selectedRows) {
      const rowId = String(row.id);
      const path = action.api.path.replace(':id', rowId);
      try {
        const putawayDraft = isPutawayInlineTable ? putawayDrafts[rowId] : null;
        const putawayQty = putawayDraft ? Number(putawayDraft.qty || 0) : null;
        if (isPutawayInlineTable && action.key === 'batchComplete') {
          if (!putawayDraft?.to_bin_id) {
            throw new Error('Putaway location is required');
          }
          if (!Number.isFinite(putawayQty) || putawayQty == null || putawayQty <= 0) {
            throw new Error('Putaway qty must be greater than 0');
          }
        }
        const requestBody = isPutawayInlineTable && action.key === 'batchComplete'
          ? {
            ...(action.api.body ?? {}),
            toBinId: putawayDraft?.to_bin_id,
            qty: putawayQty,
            notes: putawayDraft?.notes?.trim() ? putawayDraft.notes.trim() : undefined,
          }
          : (action.api.body ?? {});
        if (action.api.method === 'POST') {
          await api.post(path, requestBody);
        } else if (action.api.method === 'PATCH') {
          await api.patch(path, requestBody);
        } else if (action.api.method === 'PUT') {
          await api.put(path, requestBody);
        } else {
          await api.delete(path);
        }
        succeeded.push(rowId);
      } catch (err) {
        failed.push({ id: rowId, error: getErrorMessage(err, 'Batch action failed') });
      }
    }

    try {
      const outcome = failed.length === 0 ? 'succeeded' : succeeded.length > 0 ? 'degraded' : 'failed';
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-${action.key}-batch`,
        actionType: 'batch',
        outcome,
        entityType: tab.key,
        selectedEntityRefs: selectedRows.map((row) => ({ type: tab.key, id: String(row.id) })),
        summary: `Batch action ${action.key} for ${tab.key}: ${succeeded.length} succeeded, ${failed.length} failed.`,
        errorCode: failed.length > 0 ? 'WMS_BATCH_ACTION_PARTIAL_OR_FAILED' : undefined,
        errorMessage: failed.length > 0 ? failed.map((item) => `${item.id}: ${item.error}`).join('; ') : undefined,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          actionKey: action.key,
          label: action.label,
          method: action.api.method,
          pathTemplate: action.api.path,
          selectedCount: selectedRows.length,
          succeededIds: succeeded,
          failed,
          toStatus: action.toStatus ?? null,
        },
      });

      if (failed.length === 0) {
        setSelectedRowIds([]);
      } else {
        setSelectedRowIds(failed.map((item) => item.id));
        showInlineWarning(`${failed.length} row${failed.length === 1 ? '' : 's'} failed in ${action.label}.${failed[0]?.error ? ` ${failed[0].error}` : ''}`.trim());
      }
      await onRefetch();
    } finally {
      setBatchActionLoadingKey(null);
    }
  };

  const startInlineEdit = (row: Record<string, unknown>, fieldName: string) => {
    if (!inlineEnabled || row.id == null) return;
    const field = getEditableField(tab, fieldName);
    if (!field || field.type === 'hidden' || field.type === 'textarea') return;
    setEditingCell({ rowId: String(row.id), field: fieldName });
    setEditValue(serializeFieldValue(row[fieldName], field));
    setCellError('');
  };

  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditValue('');
    setCellError('');
  };

  const openApiResource = async ({
    pathTemplate,
    title,
    openAs,
    row,
  }: {
    pathTemplate: string;
    title: string;
    openAs?: 'csvViewer' | 'source';
    row: Record<string, unknown>;
  }) => {
    const startedAt = nowMs();
    const path = interpolateRowPath(pathTemplate, row);
    const entityId = row.id == null ? undefined : String(row.id);
    if (path.includes('//') || path.endsWith('/')) {
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-resource-open`,
        actionType: 'download',
        outcome: 'blocked',
        entityType: tab.key,
        entityId,
        summary: `Blocked WMS ${tab.key} resource open because source file is unavailable.`,
        errorCode: 'WMS_RESOURCE_PATH_UNAVAILABLE',
        errorMessage: 'Source file is not available for this row',
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          title,
          path,
          openAs: openAs ?? 'source',
          sensitivity: 'internal',
        },
      });
      showInlineWarning('Source file is not available for this row');
      return;
    }

    if (openAs !== 'csvViewer') {
      try {
        const token = getToken();
        const response = await fetch(`${BASE}${path}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!response.ok) throw new Error(await response.text());
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        await recordWmsRendererActionOutcome({
          actionId: `wms-${tab.key}-resource-open`,
          actionType: 'download',
          outcome: 'succeeded',
          entityType: tab.key,
          entityId,
          summary: `Opened WMS ${tab.key} resource.`,
          durationMs: Math.round(nowMs() - startedAt),
          metadata: {
            tabKey: tab.key,
            title,
            path,
            openAs: openAs ?? 'source',
            sensitivity: 'internal',
            bytes: blob.size,
          },
        });
      } catch (err) {
        const message = getErrorMessage(err, 'Failed to open source file');
        await recordWmsRendererActionOutcome({
          actionId: `wms-${tab.key}-resource-open`,
          actionType: 'download',
          outcome: 'failed',
          entityType: tab.key,
          entityId,
          summary: `Failed to open WMS ${tab.key} resource.`,
          errorCode: 'WMS_RESOURCE_OPEN_FAILED',
          errorMessage: message,
          durationMs: Math.round(nowMs() - startedAt),
          metadata: {
            tabKey: tab.key,
            title,
            path,
            openAs: openAs ?? 'source',
            sensitivity: 'internal',
          },
        });
        throw err;
      }
      return;
    }

    setCsvViewer({ open: true, title, headers: [], rows: [], loading: true, error: null });
    try {
      const token = getToken();
      const response = await fetch(`${BASE}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) throw new Error(await response.text());
      const text = await response.text();
      const parsed = parseCsvRows(text);
      const [headers = [], ...rows] = parsed;
      setCsvViewer({ open: true, title, headers, rows, loading: false, error: null });
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-csv-viewer-open`,
        actionType: 'download',
        outcome: 'succeeded',
        entityType: tab.key,
        entityId,
        summary: `Opened WMS ${tab.key} CSV source in viewer.`,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          title,
          path,
          openAs,
          sensitivity: 'internal',
          headers: headers.length,
          rows: rows.length,
          bytes: text.length,
        },
      });
    } catch (err: any) {
      const message = getErrorMessage(err, 'Failed to open CSV source');
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-csv-viewer-open`,
        actionType: 'download',
        outcome: 'failed',
        entityType: tab.key,
        entityId,
        summary: `Failed to open WMS ${tab.key} CSV source in viewer.`,
        errorCode: 'WMS_CSV_VIEWER_OPEN_FAILED',
        errorMessage: message,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          title,
          path,
          openAs,
          sensitivity: 'internal',
        },
      });
      setCsvViewer({
        open: true,
        title,
        headers: [],
        rows: [],
        loading: false,
        error: message,
      });
    }
  };

  const openColumnResource = async (column: ColumnDef, row: Record<string, unknown>) => {
    if (!column.openApiPath) return;
    await openApiResource({
      pathTemplate: column.openApiPath,
      title: formatValue(row[column.field], column.format) || 'CSV Source',
      openAs: column.openAs,
      row,
    });
  };

  const openActionCsvViewer = (action: ActionDef, row: Record<string, unknown>) => {
    if (!action.openApiPath) return;
    void openApiResource({
      pathTemplate: action.openApiPath,
      title: formatValue(row.file_name, undefined) || action.label || 'CSV Source',
      openAs: 'csvViewer',
      row,
    });
  };

  const openActionDetail = (action: ActionDef, row: Record<string, unknown>) => {
    if (!action.detailApiPath && !action.detailPanel) return;
    const path = action.detailApiPath ? interpolateRowPath(action.detailApiPath, row) : null;
    if (path && (path.includes('//') || path.endsWith('/'))) {
      showInlineWarning('Detail is not available for this row');
      return;
    }
    const detailIdValue = action.detailIdField ? row[action.detailIdField] : (row.id ?? row.order_id);
    if (detailIdValue == null || detailIdValue === '') {
      showInlineWarning('Detail is not available for this row');
      return;
    }
    setActionDetail({
      path,
      title: action.detailTitle ?? action.label ?? tab.detailTitle ?? 'Detail',
      rowId: String(detailIdValue),
      panel: action.detailPanel,
    });
  };

  const getPutawayDraft = (row: Record<string, unknown>) => {
    const rowId = String(row.id ?? '');
    return putawayDrafts[rowId] ?? {
      to_bin_id: String(row.to_bin_id ?? ''),
      qty: '0',
      notes: String(row.notes ?? ''),
    };
  };

  const updatePutawayDraft = (
    rowId: string,
    patch: Partial<{ to_bin_id: string; qty: string; notes: string }>,
  ) => {
    setPutawayDrafts((current) => ({
      ...current,
      [rowId]: {
        to_bin_id: current[rowId]?.to_bin_id ?? '',
        qty: current[rowId]?.qty ?? '0',
        notes: current[rowId]?.notes ?? '',
        ...patch,
      },
    }));
  };

  const savePutawayRow = async (rowId: string) => {
    const draft = putawayDrafts[rowId];
    if (!draft) return;
    const numericQty = Number(draft.qty || 0);
    if (!Number.isFinite(numericQty) || numericQty < 0) {
      showInlineWarning('Putaway qty must be 0 or greater');
      return;
    }
    setPutawaySavingRowId(rowId);
    try {
      await submitTabUpdate(tab, rowId, {
        qty: numericQty,
        toBinId: draft.to_bin_id || undefined,
        notes: draft.notes.trim() || undefined,
      });
      await onRefetch();
    } catch (err) {
      showInlineWarning(getErrorMessage(err, 'Failed to save putaway row'));
    } finally {
      setPutawaySavingRowId(null);
    }
  };

  const commitInlineEdit = async (rowId: string, field: FormFieldDef, rawValue = editValue) => {
    if (field.required && !rawValue.trim()) {
      setCellError(`${field.label} is required`);
      return;
    }

    const payload = buildUpdatePayload([field], { [field.field]: rawValue });
    if (Object.keys(payload).length === 0) {
      cancelInlineEdit();
      return;
    }

    const saveKey = `${rowId}:${field.field}`;
    setSavingCell(saveKey);
    setCellError('');
    try {
      await submitTabUpdate(tab, rowId, payload);
      cancelInlineEdit();
      onRefetch();
    } catch (err: any) {
      setCellError(err?.message ?? 'Failed to save');
    } finally {
      setSavingCell(null);
    }
  };

  if (filtered.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center py-12 text-center">
        <div>
        <Package className="mx-auto h-10 w-10 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">{emptyMessage ?? 'No records found'}</p>
        </div>
      </div>
    );
  }

  // Footer statistics: SUM for number/currency, COUNT for total
  const numericCols = visibleCols.filter(c => c.format === 'number' || c.format === 'currency');

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {inlineWarning ? (
        <div className="mx-auto mb-2 w-full max-w-4xl shrink-0 px-3">
          <div className="flex items-start justify-between gap-3 rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-900 shadow-sm">
            <div>{inlineWarning}</div>
            <button
              type="button"
              className="shrink-0 rounded p-1 text-sky-700 hover:bg-sky-100"
              onClick={() => setInlineWarning('')}
              aria-label="Close warning"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
      {showPagination && (
        <div className="mb-2 flex shrink-0 flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            Showing {pageStart + 1}-{Math.min(pageStart + pageRows.length, totalRows)} of {totalRows} rows
          </span>
          <div className="flex items-center gap-2">
            <span>Rows</span>
            <Select
              value={String(effectivePageSize)}
              onValueChange={(value) => {
                const nextPageSize = Number(value);
                if (serverPagination) {
                  onPageSizeChange?.(nextPageSize);
                  return;
                }
                setPageSize(nextPageSize);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[86px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 px-2 text-xs"
              disabled={currentPage <= 1}
              onClick={() => {
                if (serverPagination) {
                  onPageChange?.(Math.max(1, currentPage - 1));
                  return;
                }
                setPage((value) => Math.max(1, value - 1));
              }}
            >
              Prev
            </Button>
            <span className="min-w-16 text-center">Page {currentPage} / {totalPages}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 px-2 text-xs"
              disabled={currentPage >= totalPages}
              onClick={() => {
                if (serverPagination) {
                  onPageChange?.(Math.min(totalPages, currentPage + 1));
                  return;
                }
                setPage((value) => Math.min(totalPages, value + 1));
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {selectionEnabled && selectedRows.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <span>{selectedRows.length} selected</span>
          <div className="flex flex-wrap items-center gap-2">
            {batchActions.map((action) => {
              const ActionIcon = getIcon(action.icon);
              const isLoading = batchActionLoadingKey === action.key;
              return (
                <Button
                  key={action.key}
                  type="button"
                  size="sm"
                  variant={action.variant ?? 'outline'}
                  className="h-7 bg-white px-2 text-xs"
                  disabled={batchDeleting || batchActionLoadingKey != null}
                  onClick={() => void handleBatchAction(action)}
                >
                  {isLoading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : ActionIcon ? <ActionIcon className="mr-1 h-3 w-3" /> : null}
                  {action.label}
                </Button>
              );
            })}
            {batchDeleteEnabled && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 border-amber-300 bg-white px-2 text-xs text-amber-900 hover:bg-amber-100"
                disabled={batchDeleting || batchActionLoadingKey != null}
                onClick={() => void handleBatchDelete()}
              >
                {batchDeleting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Trash2 className="mr-1 h-3 w-3" />}
                Remove
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="runtime-grid-scrollbar boox-x-scroll min-h-0 min-w-0 flex-1 overflow-auto rounded-md border bg-background">
      <table className="w-max min-w-full text-sm" style={{ minWidth: tableMinWidth }}>
        <thead>
          <tr className="border-b bg-muted/50">
            {detailInlineEnabled && (
              <th className="w-10 whitespace-nowrap px-2 py-2 text-left text-xs font-medium text-muted-foreground" />
            )}
            {selectionEnabled && (
              <th className="w-10 whitespace-nowrap px-2 py-2 text-left text-xs font-medium text-muted-foreground">
                <Checkbox
                  checked={allFilteredSelected}
                  disabled={selectableRows.length === 0 || batchDeleting || batchActionLoadingKey != null}
                  aria-label="Select all rows"
                  onCheckedChange={(checked) => toggleAllFilteredRows(checked === true)}
                />
              </th>
            )}
            {hasActions && actionColumnPosition === 'start' && (
              <th
                className={`whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground ${freezeActionColumn ? 'sticky left-0 z-20 bg-background shadow-[1px_0_0_0_theme(colors.border)]' : ''}`}
                style={{ width: actionColumnWidth }}
              >
                Actions
              </th>
            )}
            {visibleCols.map(col => {
              const isSorted = sort.field === col.field;
              const canSort = col.sortable !== false; // sortable by default
              return (
                <th
                  key={col.field}
                  className={`whitespace-nowrap text-left px-3 py-2 text-xs font-medium text-muted-foreground select-none ${canSort ? 'cursor-pointer hover:text-foreground' : ''}`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={() => canSort && handleSort(col.field)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {canSort && isSorted && (
                      sort.direction === 'asc'
                        ? <ChevronUp className="h-3 w-3" />
                        : <ChevronDown className="h-3 w-3" />
                    )}
                  </span>
                </th>
              );
            })}
            {hasActions && actionColumnPosition === 'end' && (
              <th
                className={`whitespace-nowrap px-3 py-2 text-right text-xs font-medium text-muted-foreground ${freezeActionColumn ? 'sticky right-0 z-20 bg-background shadow-[-1px_0_0_0_theme(colors.border)]' : ''}`}
                style={{ width: actionColumnWidth }}
              >
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row, idx) => {
            const status = statusField ? String(row[statusField] ?? '') : '';
            const visibleActions = rowActions?.filter(a =>
              !a.showWhen || a.showWhen.includes(status.toUpperCase())
            );
            const rowKey = String(row.id || pageStart + idx);
            const isExpanded = expandedRowId === rowKey;
            const handleRowDoubleClick = () => {
              if (disableRowDetailOpen) return;
              if (prefersMasterDetailSheet && row.id != null) {
                setDetailRowId(String(row.id));
                return;
              }
              if (dialogEnabled && row.id != null) {
                setDialogRow(row);
                return;
              }
              if (!dialogEnabled && detailDialogEnabled && row.id != null) {
                setDetailRowId(String(row.id));
              }
            };
            return (
              <Fragment key={rowKey}>
                <tr key={rowKey} className="border-b hover:bg-muted/30 transition-colors" onDoubleClick={handleRowDoubleClick}>
                  {detailInlineEnabled && (
                    <td className="whitespace-nowrap px-2 py-2 align-top">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => setExpandedRowId(isExpanded ? null : rowKey)}
                        aria-label={isExpanded ? 'Collapse lines' : 'Expand lines'}
                      >
                        {isExpanded ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      </Button>
                    </td>
                  )}
                  {selectionEnabled && (
                    <td className="whitespace-nowrap px-2 py-2 align-top">
                      <Checkbox
                        checked={row.id != null && selectedRowIds.includes(String(row.id))}
                        disabled={row.id == null || batchDeleting || batchActionLoadingKey != null}
                        aria-label={`Select row ${rowKey}`}
                        onCheckedChange={(checked) => {
                          if (row.id != null) toggleRowSelection(String(row.id), checked === true);
                        }}
                      />
                    </td>
                  )}
                  {hasActions && actionColumnPosition === 'start' && (
                    <td className={`whitespace-nowrap px-3 py-2 text-left ${freezeActionColumn ? 'sticky left-0 z-10 bg-background shadow-[1px_0_0_0_theme(colors.border)]' : ''}`}>
                      <div className="flex items-center gap-1">
                        {!simpleMode && dialogEnabled && row.id != null && (
                          <Button
                            size={rowActionStyle === 'icon' ? 'icon' : 'sm'}
                            variant="outline"
                            className={rowActionStyle === 'icon' ? 'h-7 w-7' : 'h-7 text-xs'}
                            onClick={() => setDialogRow(row)}
                            title="Edit"
                            aria-label="Edit"
                          >
                            <Pencil className={rowActionStyle === 'icon' ? 'h-3 w-3' : 'mr-1 h-3 w-3'} />
                            {rowActionStyle === 'icon' ? null : 'Edit'}
                          </Button>
                        )}
                        {!simpleMode && !disableRowDetailOpen && detailDialogEnabled && row.id != null && (
                          <Button
                            size={rowActionStyle === 'icon' ? 'icon' : 'sm'}
                            variant="outline"
                            className={rowActionStyle === 'icon' ? 'h-7 w-7' : 'h-7 text-xs'}
                            onClick={() => setDetailRowId(String(row.id))}
                            title="View"
                            aria-label="View"
                          >
                            <Search className={rowActionStyle === 'icon' ? 'h-3 w-3' : 'mr-1 h-3 w-3'} />
                            {rowActionStyle === 'icon' ? null : 'View'}
                          </Button>
                        )}
                        {visibleActions?.map(a => (
                          <RowActionButton
                            key={a.key}
                            action={a}
                            row={row}
                            statuses={statuses}
                            onRefetch={onRefetch}
                            onOpenCsvViewer={openActionCsvViewer}
                            onOpenDetail={openActionDetail}
                            iconOnly={rowActionStyle === 'icon'}
                          />
                        ))}
                      </div>
                    </td>
                  )}
                  {visibleCols.map((col, colIndex) => {
                    const field = getEditableField(tab, col.field);
                    const isEditing = editingCell?.rowId === String(row.id) && editingCell.field === col.field;
                    const isSaving = savingCell === `${String(row.id)}:${col.field}`;
                    const inlineEditable = inlineEnabled && !!field && field.type !== 'hidden' && field.type !== 'textarea' && row.id != null;
                    const detailTargetId = String(
                      (pageKey === 'stockLedger' && col.field === 'inventory_code'
                        ? (row.inventory_id ?? row.id)
                        : row.id) ?? '',
                    );
                    const opensDetail = !disableRowDetailOpen
                      && detailDialogEnabled
                      && detailTargetId.length > 0
                      && (col.openDetailOnClick || (simpleMode && colIndex === 0));
                    const opensInventoryBreakdown = pageKey === 'inventory'
                      && col.field === 'sku_code'
                      && (row.sku_code != null || row.sku_id != null);

                    return (
                      <td
                        key={col.field}
                        className={`whitespace-nowrap px-3 py-2 align-top ${inlineEditable ? 'cursor-text' : ''}`}
                        onDoubleClick={() => {
                          if (!dialogEnabled) startInlineEdit(row, col.field);
                        }}
                        title={dialogEnabled ? 'Double click row to edit' : inlineEditable ? 'Double click to edit' : undefined}
                      >
                        {isPutawayInlineTable && row.id != null && col.field === 'to_bin_code' ? (
                          <select
                            className="h-8 min-w-[140px] rounded border border-input bg-background px-2 text-sm"
                            value={getPutawayDraft(row).to_bin_id}
                            onChange={(event) => updatePutawayDraft(String(row.id), { to_bin_id: event.target.value })}
                          >
                            <option value="">Select</option>
                            {(dynamicOptions.to_bin_id ?? []).map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        ) : isPutawayInlineTable && row.id != null && col.field === 'putaway_qty' ? (
                          <Input
                            className="h-8 min-w-[90px] text-sm"
                            type="number"
                            min="0"
                            step="0.001"
                            value={getPutawayDraft(row).qty}
                            onChange={(event) => updatePutawayDraft(String(row.id), { qty: event.target.value })}
                          />
                        ) : isPutawayInlineTable && row.id != null && col.field === 'notes' ? (
                          <Input
                            className="h-8 min-w-[160px] text-sm"
                            type="text"
                            value={getPutawayDraft(row).notes}
                            onChange={(event) => updatePutawayDraft(String(row.id), { notes: event.target.value })}
                          />
                        ) : isPutawayInlineTable && col.field === 'ready_qty' ? (
                          <span className="text-sm">{formatValue(row.ready_qty, col.format)}</span>
                        ) : isEditing && field ? (
                          <div className="space-y-1">
                            {field.type === 'select' ? (
                              <select
                                autoFocus
                                className="h-8 min-w-[120px] rounded border border-input bg-background px-2 text-sm"
                                value={editValue}
                                onChange={(event) => {
                                  const nextValue = event.target.value;
                                  setEditValue(nextValue);
                                  void commitInlineEdit(String(row.id), field, nextValue);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Escape') {
                                    event.preventDefault();
                                    cancelInlineEdit();
                                  }
                                }}
                              >
                                {buildOptions(field, dynamicOptions).map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            ) : (
                              <Input
                                autoFocus
                                className="h-8 min-w-[120px] text-sm"
                                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                value={editValue}
                                onChange={(event) => setEditValue(event.target.value)}
                                onBlur={() => {
                                  if (!savingCell) void commitInlineEdit(String(row.id), field);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    void commitInlineEdit(String(row.id), field);
                                  }
                                  if (event.key === 'Escape') {
                                    event.preventDefault();
                                    cancelInlineEdit();
                                  }
                                }}
                              />
                            )}
                            {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                            {cellError && <p className="text-[10px] text-red-600">{cellError}</p>}
                          </div>
                        ) : col.format === 'status' ? (
                          <StatusBadge value={String(row[col.field] ?? '')} statuses={statuses} />
                        ) : (
                          col.openApiPath ? (
                            <button
                              type="button"
                              className="inline-flex max-w-[260px] items-center rounded border border-blue-200 bg-blue-50 px-2 py-1 text-left text-sm font-medium text-blue-700 hover:bg-blue-100 hover:underline"
                              onClick={() => void openColumnResource(col, row)}
                              title="View CSV"
                            >
                              {formatValue(row[col.field], col.format)}
                            </button>
                          ) : opensInventoryBreakdown ? (
                            <button
                              type="button"
                              className="text-sm font-medium text-primary hover:underline"
                              onClick={() => setInventoryBreakdownTarget({
                                skuCode: row.sku_code == null ? undefined : String(row.sku_code),
                                skuName: row.sku_name == null ? undefined : String(row.sku_name),
                              })}
                            >
                              {formatValue(row[col.field], col.format)}
                            </button>
                          ) : opensDetail ? (
                            <button
                              type="button"
                              className="text-sm font-medium text-primary hover:underline"
                              onClick={() => setDetailRowId(detailTargetId)}
                            >
                              {formatValue(row[col.field], col.format)}
                            </button>
                          ) : (
                            <span className={`text-sm ${inlineEditable ? 'hover:underline decoration-dotted' : ''}`}>
                              {formatValue(row[col.field], col.format)}
                            </span>
                          )
                        )}
                      </td>
                    );
                  })}
                  {hasActions && actionColumnPosition === 'end' && (
                    <td className={`whitespace-nowrap px-3 py-2 text-right ${freezeActionColumn ? 'sticky right-0 z-10 bg-background shadow-[-1px_0_0_0_theme(colors.border)]' : ''}`}>
                      <div className="flex items-center justify-end gap-1">
                        {isPutawayInlineTable && row.id != null && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={putawaySavingRowId === String(row.id)}
                            onClick={() => void savePutawayRow(String(row.id))}
                          >
                            {putawaySavingRowId === String(row.id) ? (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            ) : (
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                            )}
                            Confirm
                          </Button>
                        )}
                        {!simpleMode && dialogEnabled && row.id != null && (
                          <Button
                            size={rowActionStyle === 'icon' ? 'icon' : 'sm'}
                            variant="outline"
                            className={rowActionStyle === 'icon' ? 'h-7 w-7' : 'h-7 text-xs'}
                            onClick={() => setDialogRow(row)}
                            title="Edit"
                            aria-label="Edit"
                          >
                            <Pencil className={rowActionStyle === 'icon' ? 'h-3 w-3' : 'mr-1 h-3 w-3'} />
                            {rowActionStyle === 'icon' ? null : 'Edit'}
                          </Button>
                        )}
                        {!simpleMode && !disableRowDetailOpen && detailDialogEnabled && row.id != null && (
                          <Button
                            size={rowActionStyle === 'icon' ? 'icon' : 'sm'}
                            variant="outline"
                            className={rowActionStyle === 'icon' ? 'h-7 w-7' : 'h-7 text-xs'}
                            onClick={() => setDetailRowId(String(row.id))}
                            title="View"
                            aria-label="View"
                          >
                            <Search className={rowActionStyle === 'icon' ? 'h-3 w-3' : 'mr-1 h-3 w-3'} />
                            {rowActionStyle === 'icon' ? null : 'View'}
                          </Button>
                        )}
                        {visibleActions?.map(a => (
                          <RowActionButton
                            key={a.key}
                            action={a}
                            row={row}
                            statuses={statuses}
                            onRefetch={onRefetch}
                            onOpenCsvViewer={openActionCsvViewer}
                            onOpenDetail={openActionDetail}
                            iconOnly={rowActionStyle === 'icon'}
                          />
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
                {detailInlineEnabled && isExpanded && row.id != null && (
                  <tr key={`${rowKey}-detail`} className="border-b bg-muted/20">
                    <td colSpan={visibleCols.length + (hasActions ? 1 : 0) + 1 + (selectionEnabled ? 1 : 0)} className="px-3 py-3">
                      <InlineRecordDetail tab={tab} rowId={String(row.id)} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        {!tab.hideFooter && (numericCols.length > 0 || filtered.length > 0) && (
          <tfoot>
            <tr className="border-t-2 bg-muted/30 font-medium">
              {detailInlineEnabled && <td className="px-2 py-2" />}
              {selectionEnabled && <td className="px-2 py-2" />}
              {visibleCols.map((col, ci) => {
                if (col.format === 'number' || col.format === 'currency') {
                  const total = pageRows.reduce((s, r) => s + (Number(r[col.field]) || 0), 0);
                  return (
                    <td key={col.field} className="px-3 py-2 text-sm">
                      {col.format === 'currency'
                        ? `$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                        : total.toLocaleString()
                      }
                    </td>
                  );
                }
                // First column: show row count
                if (ci === 0) {
                  return (
                    <td key={col.field} className="px-3 py-2 text-xs text-muted-foreground">
                      {showPagination ? `${pageRows.length} shown / ${totalRows} rows` : `${totalRows} rows`}
                    </td>
                  );
                }
                return <td key={col.field} className="px-3 py-2" />;
              })}
              {hasActions && <td className={`px-3 py-2 ${freezeActionColumn ? (actionColumnPosition === 'start' ? 'sticky left-0 bg-background shadow-[1px_0_0_0_theme(colors.border)]' : 'sticky right-0 bg-background shadow-[-1px_0_0_0_theme(colors.border)]') : ''}`} />}
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      {dialogEnabled && (
        <RecordEditDialog
          tab={tab}
          row={dialogRow}
          open={isRenderedTabActive && dialogRow != null}
          onClose={() => setDialogRow(null)}
          onSuccess={onRefetch}
        />
      )}
      {detailDialogEnabled && (
        <RecordDetailDialog
          tab={tab}
          rowId={detailRowId}
          open={isRenderedTabActive && detailRowId != null}
          onClose={() => {
            setDetailRowId(null);
            onClearRequestedOpenRowId?.();
            void onRefetch();
          }}
        />
      )}
      <ActionRecordDetailDialog
        tab={tab}
        panel={actionDetail?.panel}
        title={actionDetail?.title ?? 'Detail'}
        path={actionDetail?.path ?? null}
        rowId={actionDetail?.rowId ?? ''}
        open={isRenderedTabActive && actionDetail != null}
        onClose={() => {
          setActionDetail(null);
          void onRefetch();
        }}
      />
      <Dialog
        open={csvViewer.open}
        onOpenChange={(open) => {
          if (!open) setCsvViewer(emptyCsvViewerState);
        }}
      >
        <DialogContent dock="right" className="flex h-[min(88vh,860px)] w-[min(100vw-32px,1180px)] max-w-none flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>{csvViewer.title || 'CSV Source'}</DialogTitle>
            <DialogDescription>Original uploaded PO source file.</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {csvViewer.loading ? (
              <div className="flex min-h-[220px] items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading CSV...
              </div>
            ) : csvViewer.error ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {csvViewer.error}
              </div>
            ) : (
              <div className="overflow-auto rounded-md border">
                <table className="min-w-full text-xs">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      {csvViewer.headers.map((header, index) => (
                        <th key={`${header}-${index}`} className="whitespace-nowrap border-b border-r px-2 py-2 text-left font-medium">
                          {header || `Column ${index + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvViewer.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-b last:border-b-0">
                        {csvViewer.headers.map((_header, columnIndex) => (
                          <td key={columnIndex} className="max-w-[280px] whitespace-pre-wrap border-r px-2 py-2 align-top">
                            {row[columnIndex] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <InventoryBreakdownSheet
        target={inventoryBreakdownTarget}
        onClose={() => setInventoryBreakdownTarget(null)}
      />
    </div>
  );
}

// ─── Tab Content ───────────────────────────────────────────────────────────

function TabContent({
  pageKey, tab, statuses, search, onRefetch,
  requestParams,
  data, loading, error,
  hideKpiCards = false,
  simpleMode = false,
  pagination,
  onPageChange,
  onPageSizeChange,
  requestedOpenRowId,
  onClearRequestedOpenRowId,
}: {
  pageKey: string;
  tab: TabDef;
  statuses: StatusDef[];
  search: string;
  onRefetch: () => void;
  requestParams: Record<string, string | number>;
  data: Record<string, unknown>[];
  loading: boolean;
  error: string | null;
  hideKpiCards?: boolean;
  simpleMode?: boolean;
  pagination?: { total: number; page: number; limit: number; hasMore?: boolean; server: boolean } | null;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  requestedOpenRowId?: string | null;
  onClearRequestedOpenRowId?: () => void;
}) {
  // Compute KPI values from data
  const computedData = { ...({ _computed: computeKpis(tab, data) }) };

  const handleExportXlsx = useCallback(async (layout: 'standard' | 'legacy' = 'standard') => {
    const endpoint = layout === 'legacy' ? (tab.legacyExportEndpoint ?? tab.exportEndpoint) : tab.exportEndpoint;
    if (!endpoint) return;
    const startedAt = nowMs();
    const token = getToken();
    const params = new URLSearchParams(
      Object.entries(requestParams).map(([key, value]) => [key, String(value)]),
    );
    if (layout === 'legacy') params.set('layout', 'legacy');
    const url = `${BASE}${endpoint}${params.toString() ? '?' + params.toString() : ''}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const disposition = res.headers.get('Content-Disposition') ?? '';
      const match = disposition.match(/filename="([^"]+)"/);
      a.download = match?.[1] ?? `${tab.key}-export.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-export-xlsx`,
        actionType: 'export',
        outcome: 'succeeded',
        entityType: tab.key,
        summary: `Exported WMS ${tab.key} XLSX.`,
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          layout,
          endpoint,
          requestParams,
          filename: a.download,
          bytes: blob.size,
        },
      });
    } catch (err) {
      await recordWmsRendererActionOutcome({
        actionId: `wms-${tab.key}-export-xlsx`,
        actionType: 'export',
        outcome: 'failed',
        entityType: tab.key,
        summary: `WMS ${tab.key} XLSX export failed.`,
        errorCode: 'WMS_EXPORT_XLSX_FAILED',
        errorMessage: getErrorMessage(err, 'Export failed'),
        durationMs: Math.round(nowMs() - startedAt),
        metadata: {
          tabKey: tab.key,
          layout,
          endpoint,
          requestParams,
        },
      });
      console.error('Export error', err);
    }
  }, [requestParams, tab]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {tab.exportEndpoint && (
        <div className={simpleMode ? 'mb-2 flex shrink-0 justify-end' : 'flex shrink-0 justify-end px-4 pt-3'}>
          <Button size="sm" variant="outline" onClick={() => void handleExportXlsx()}>
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export XLSX
          </Button>
          {tab.legacyExportEndpoint && (
            <Button size="sm" variant="outline" className="ml-2" onClick={() => void handleExportXlsx('legacy')}>
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Legacy XLSX
            </Button>
          )}
        </div>
      )}
      {!hideKpiCards && tab.kpiCards && tab.kpiCards.length > 0 && (
        <KpiCards cards={tab.kpiCards} data={computedData} />
      )}

      {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="flex min-h-0 flex-1 items-center justify-center py-8 text-center">
          <div>
          <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
      ) : tab.columns.length === 0 ? (
        <div className="flex min-h-0 flex-1 items-center justify-center py-8 text-center">
          <p className="text-sm text-muted-foreground">Dashboard overview — see KPI cards above</p>
        </div>
      ) : (
        <DslTable
          pageKey={pageKey}
          tab={tab}
          columns={tab.columns}
          rows={data}
          statusField={tab.statusField}
          statuses={statuses}
          rowActions={tab.rowActions}
          search={search}
          emptyMessage={tab.emptyMessage}
          onRefetch={onRefetch}
          simpleMode={simpleMode}
          pagination={pagination}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          requestedOpenRowId={requestedOpenRowId}
          onClearRequestedOpenRowId={onClearRequestedOpenRowId}
        />
      )}
    </div>
  );
}

// ─── KPI Computation ───────────────────────────────────────────────────────

function computeKpis(tab: TabDef, rows: Record<string, unknown>[]): Record<string, number> {
  const today = new Date().toDateString();
  const kpis: Record<string, number> = {};

  // Generic status counters
  kpis.total = rows.length;
  if (tab.statusField) {
    const sf = tab.statusField;
    kpis.pending = rows.filter(r => ['NEW', 'DRAFT', 'PENDING', 'PLANNED'].includes(String(r[sf]).toUpperCase())).length;
    kpis.inProgress = rows.filter(r => ['IN_PROGRESS', 'SUBMITTED', 'RECEIVING', 'QC_PENDING', 'PUTAWAY_PENDING', 'PICKING', 'ALLOCATED', 'REVIEWING', 'PENDING_APPROVAL'].includes(String(r[sf]).toUpperCase())).length;
    kpis.inPicking = rows.filter(r => ['PICKING', 'ALLOCATED'].includes(String(r[sf]).toUpperCase())).length;
    kpis.inReceiving = rows.filter(r => ['RECEIVING', 'SUBMITTED', 'QC_PENDING', 'PUTAWAY_PENDING'].includes(String(r[sf]).toUpperCase())).length;
    kpis.completed = rows.filter(r => ['COMPLETED', 'RECEIVED', 'SHIPPED'].includes(String(r[sf]).toUpperCase())).length;
  }

  // Today calculations
  kpis.expectedToday = rows.filter(r => {
    try { return r.expected_date && new Date(String(r.expected_date)).toDateString() === today; } catch { return false; }
  }).length;
  kpis.completedToday = rows.filter(r => {
    const s = String(r[tab.statusField ?? 'status'] ?? '').toUpperCase();
    try { return ['RECEIVED', 'COMPLETED', 'SHIPPED'].includes(s) && r.updated_at && new Date(String(r.updated_at)).toDateString() === today; } catch { return false; }
  }).length;
  kpis.shippedToday = rows.filter(r => {
    try { return String(r.status ?? '').toUpperCase() === 'SHIPPED' && r.updated_at && new Date(String(r.updated_at)).toDateString() === today; } catch { return false; }
  }).length;

  // Inventory KPIs
  kpis.totalSkus = new Set(rows.map(r => r.sku_code)).size;
  kpis.totalQty = rows.reduce((s, r) => s + (Number(r.qty_on_hand) || 0), 0);
  kpis.totalReserved = rows.reduce((s, r) => s + (Number(r.qty_allocated) || 0), 0);
  kpis.totalAvailable = rows.reduce((s, r) => {
    const explicit = Number(r.qty_available);
    if (Number.isFinite(explicit)) return s + explicit;
    return s + Math.max((Number(r.qty_on_hand) || 0) - (Number(r.qty_allocated) || 0), 0);
  }, 0);

  return kpis;
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN RENDERER
// ═══════════════════════════════════════════════════════════════════════════

interface WmsPageRendererProps {
  pageDef: WmsPageDef;
  showRuntimeChrome?: boolean;
}

export default function WmsPageRenderer({ pageDef, showRuntimeChrome = true }: WmsPageRendererProps) {
  const isMobile = useRenderMode() === 'mobile';
  const navigate = useNavigate();
  const renderedTab = useRenderedTab();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParamKey = `${pageDef.key}Tab`;
  const renderedTabSearchParams = useMemo(() => {
    if (!renderedTab?.tabPath) return null;
    const query = renderedTab.tabPath.split('?')[1] ?? '';
    return new URLSearchParams(query);
  }, [renderedTab?.tabPath]);
  const effectiveSearchParams = renderedTab && !renderedTab.isActive
    ? (renderedTabSearchParams ?? searchParams)
    : searchParams;
  const wmsTemplateResolutionContext = useMemo(
    () => buildWmsTemplateResolutionContextFromSearchParams(effectiveSearchParams),
    [effectiveSearchParams],
  );
  const activeWmsTemplate = useWmsProfile(wmsTemplateResolutionContext);
  const pendingLocalTabRef = useRef<string | null>(null);
  const resolveRequestedTab = useCallback((params: URLSearchParams) => {
    const scopedTab = resolveLegacyTabKey(pageDef.key, params.get(tabParamKey));
    if (scopedTab && pageDef.tabs.some((tab) => tab.key === scopedTab)) {
      return scopedTab;
    }

    const legacyTab = resolveLegacyTabKey(pageDef.key, params.get('tab'));
    if (legacyTab && pageDef.tabs.some((tab) => tab.key === legacyTab)) {
      return legacyTab;
    }

    return null;
  }, [pageDef.key, pageDef.tabs, tabParamKey]);
  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = resolveRequestedTab(effectiveSearchParams);
    if (requestedTab) {
      return requestedTab;
    }
    return pageDef.tabs[0]?.key ?? '';
  });
  const [pageSearch, setPageSearch] = useState('');
  const [tabSearch, setTabSearch] = useState<Record<string, string>>({});
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() => createInitialFilterValues(pageDef.filters));
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [scanning, setScanning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [openMaterialRequestCreateKey, setOpenMaterialRequestCreateKey] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showCreateChooser, setShowCreateChooser] = useState(false);
  const [runtimeWorkspace, setRuntimeWorkspace] = useState<WmsRuntimeWorkspaceTemplate | null>(null);
  const [runtimeWorkspaceStatus, setRuntimeWorkspaceStatus] = useState<'loading' | 'published' | 'missing'>('loading');
  const [runtimeReportModel, setRuntimeReportModel] = useState<WmsRuntimeReportModel | null>(null);
  const [runtimeReportStatus, setRuntimeReportStatus] = useState<'idle' | 'loading' | 'published' | 'missing'>('idle');
  const [runtimeBillingModel, setRuntimeBillingModel] = useState<WmsRuntimeBillingModel | null>(null);
  const [runtimeBillingStatus, setRuntimeBillingStatus] = useState<'idle' | 'loading' | 'published' | 'missing'>('idle');
  const scanRef = useRef<HTMLInputElement>(null);
  const importWorkflow = pageDef.importWorkflow;
  const importDialogType = importWorkflow?.type;
  const importSupported = Boolean(importWorkflow && importDialogType);
  const importButtonLabel = importWorkflow?.buttonLabel ?? (importDialogType ? getImportButtonLabel(importDialogType) : 'Import');
  const isCompactList = pageDef.compactList === true;
  const requestedOpenRowId = effectiveSearchParams.get('open');
  const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isWpSimpleOpsPage = currentPathname.startsWith('/wp') && isCompactList && WP_SIMPLE_OPS_PAGES.has(pageDef.key);
  const useGridFilterLayout = isWpSimpleOpsPage || pageDef.filterLayoutColumns === 6;
  const filterGridClass = pageDef.filterLayoutColumns === 6
    ? 'grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6'
    : 'grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6';
  const filterFieldClass = useGridFilterLayout ? 'min-w-0 space-y-1' : 'min-w-[180px] space-y-1';
  const searchScope = pageDef.searchScope ?? 'page';
  const clearSearchState = useCallback(() => {
    setPageSearch('');
    setTabSearch({});
  }, []);
  const getTabSearchValue = useCallback((tabKey: string) => (
    searchScope === 'tab' ? (tabSearch[tabKey] ?? '') : pageSearch
  ), [pageSearch, searchScope, tabSearch]);
  const setSearchValue = useCallback((nextValue: string, tabKey?: string) => {
    if (searchScope === 'tab' && tabKey) {
      setTabSearch((current) => ({ ...current, [tabKey]: nextValue }));
      return;
    }
    setPageSearch(nextValue);
  }, [searchScope]);
  const clearRequestedOpenRowId = useCallback(() => {
    if (!searchParams.get('open')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    void setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ── Per-tab data cache ──────────────────────────────────────────────────
  const [tabData, setTabData] = useState<Record<string, {
    rows: Record<string, unknown>[];
    loading: boolean;
    error: string | null;
    rawData?: Record<string, unknown>;
    pagination?: { total: number; page: number; limit: number; hasMore?: boolean; server: boolean };
  }>>({});
  const [tabPaging, setTabPaging] = useState<Record<string, { page: number; pageSize: number }>>({});

  // Reset state when pageDef changes (navigating between WMS pages)
  useEffect(() => {
    const requestedTab = resolveRequestedTab(effectiveSearchParams);
    if (requestedTab) {
      setActiveTab(requestedTab);
    } else {
      setActiveTab(pageDef.tabs[0]?.key ?? '');
    }
    clearSearchState();
    setFilterValues(createInitialFilterValues(pageDef.filters));
    setFilterOptions({});
    setTabData({});
    setTabPaging({});
  }, [clearSearchState, pageDef.filters, pageDef.key]);

  const getTabPagingState = useCallback((tab: TabDef) => {
    const current = tabPaging[tab.key];
    return {
      page: current?.page ?? 1,
      pageSize: current?.pageSize ?? tab.pagination?.pageSize ?? 50,
    };
  }, [tabPaging]);

  useEffect(() => {
    let cancelled = false;

    if (!pageDef.filters || pageDef.filters.length === 0) {
      setFilterOptions({});
      return;
    }

    void loadFilterOptions(pageDef.filters)
      .then((options) => {
        if (!cancelled) setFilterOptions(options);
      })
      .catch(() => {
        if (!cancelled) setFilterOptions({});
      });

    return () => {
      cancelled = true;
    };
  }, [pageDef.filters]);

  useEffect(() => {
    if (!importSupported || !importDialogType) return;
    if (effectiveSearchParams.get('openImport') !== '1') return;
    if (importWorkflow?.openTab) {
      setActiveTab(importWorkflow.openTab);
    }
    setShowImport(true);
  }, [effectiveSearchParams, importDialogType, importSupported, importWorkflow?.openTab]);

  useEffect(() => {
    if (!importSupported && showImport) {
      setShowImport(false);
    }
  }, [importSupported, showImport]);

  useEffect(() => {
    const requestedTab = resolveRequestedTab(effectiveSearchParams);
    if (pendingLocalTabRef.current) {
      if (requestedTab === pendingLocalTabRef.current) {
        pendingLocalTabRef.current = null;
      } else if (activeTab === pendingLocalTabRef.current) {
        return;
      }
    }

    if (requestedTab && requestedTab !== activeTab) {
      setActiveTab(requestedTab);
      return;
    }

    if (!pageDef.tabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(pageDef.tabs[0]?.key ?? '');
    }
  }, [activeTab, effectiveSearchParams, pageDef.tabs, resolveRequestedTab]);

  useEffect(() => {
    if (!activeTab) return;
    if (!pageDef.tabs.some((tab) => tab.key === activeTab)) return;
    if (pageDef.tabs.length <= 1) return;
    // Only update URL when this tab is the active MDI tab.
    // Background tabs (display:none but still mounted) must not fight over the shared URL.
    if (renderedTab && !renderedTab.isActive) return;

    const currentScopedTab = searchParams.get(tabParamKey);
    const legacyTab = searchParams.get('tab');
    if (currentScopedTab === activeTab && !legacyTab) {
      if (pendingLocalTabRef.current === activeTab) pendingLocalTabRef.current = null;
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set(tabParamKey, activeTab);
    if (next.get('tab') === activeTab) {
      next.delete('tab');
    }
    setSearchParams(next, { replace: true });
  }, [activeTab, pageDef.tabs, renderedTab, searchParams, setSearchParams, tabParamKey]);

  const handleActiveTabChange = useCallback((nextTab: string) => {
    if (!pageDef.tabs.some((tab) => tab.key === nextTab)) return;
    pendingLocalTabRef.current = nextTab;
    setActiveTab(nextTab);

    if (pageDef.tabs.length <= 1) return;
    if (renderedTab && !renderedTab.isActive) return;
    const next = new URLSearchParams(searchParams);
    next.set(tabParamKey, nextTab);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [pageDef.tabs, renderedTab, searchParams, setSearchParams, tabParamKey]);

  const getTabRequestParams = useCallback((tab: TabDef) => {
    const params = buildTabRequestParams(tab, pageDef.filters, filterValues);
    const paging = getTabPagingState(tab);
    if (tab.pagination) {
      params.page = paging.page;
      params.limit = paging.pageSize;
    }
    const trimmedSearch = getTabSearchValue(tab.key).trim();
    if (trimmedSearch) params.search = trimmedSearch;
    return params;
  }, [filterValues, getTabPagingState, getTabSearchValue, pageDef.filters]);

  // Fetch data for a tab
  const fetchTabData = useCallback(async (tab: TabDef, isRefresh = false) => {
    if (!tab.apiEndpoint) return;

    setTabData(prev => ({
      ...prev,
      [tab.key]: { ...(prev[tab.key] ?? { rows: [], error: null }), loading: !isRefresh },
    }));

    try {
      const requestParams = getTabRequestParams(tab);
      const params = Object.keys(requestParams).length > 0
        ? '?' + new URLSearchParams(Object.entries(requestParams).map(([key, value]) => [key, String(value)])).toString()
        : '';
      const res = await api.get<any>(`${tab.apiEndpoint}${params}`);

      let rows: Record<string, unknown>[];
      if (tab.dataPath) {
        rows = getNestedValue(res, tab.dataPath) as Record<string, unknown>[] ?? [];
      } else if (Array.isArray(res)) {
        rows = res;
      } else if (res?.items && Array.isArray(res.items)) {
        rows = res.items;
      } else {
        rows = [];
      }

      const pagination = !Array.isArray(res) && res?.items && Array.isArray(res.items) && typeof res.total === 'number'
        ? {
            total: Number(res.total ?? rows.length),
            page: Number(res.page ?? getTabPagingState(tab).page),
            limit: Number(res.limit ?? getTabPagingState(tab).pageSize),
            hasMore: Boolean(res.hasMore),
            server: true,
          }
        : undefined;

      setTabData(prev => ({
        ...prev,
        [tab.key]: { rows, loading: false, error: null, rawData: Array.isArray(res) ? undefined : res, pagination },
      }));
    } catch (err: any) {
      setTabData(prev => ({
        ...prev,
        [tab.key]: { rows: [], loading: false, error: err?.message ?? 'Failed to load' },
      }));
    }
  }, [getTabPagingState, getTabRequestParams]);

  // Initial fetch for all tabs
  useEffect(() => {
    // Group tabs that share same endpoint to avoid duplicate calls
    const endpointGroups = new Map<string, TabDef[]>();
    for (const tab of pageDef.tabs) {
      if (!tab.apiEndpoint) continue;
      const key = `${tab.apiEndpoint}|${JSON.stringify(getTabRequestParams(tab))}`;
      if (!endpointGroups.has(key)) endpointGroups.set(key, []);
      endpointGroups.get(key)!.push(tab);
    }

    // For shared endpoints (e.g. dashboard), fetch once and distribute
    for (const [, tabs] of endpointGroups) {
      if (tabs.length === 1) {
        fetchTabData(tabs[0]!);
      } else {
        // Fetch once, distribute
        const firstTab = tabs[0]!;
        (async () => {
          try {
            const requestParams = getTabRequestParams(firstTab);
            const params = Object.keys(requestParams).length > 0
              ? '?' + new URLSearchParams(Object.entries(requestParams).map(([key, value]) => [key, String(value)])).toString()
              : '';
            const res = await api.get<any>(`${firstTab.apiEndpoint}${params}`);

            for (const tab of tabs) {
              let rows: Record<string, unknown>[];
              if (tab.dataPath) {
                rows = (getNestedValue(res, tab.dataPath) as Record<string, unknown>[]) ?? [];
              } else if (Array.isArray(res)) {
                rows = res;
              } else if (res?.items) {
                rows = res.items;
              } else {
                rows = [];
              }
              setTabData(prev => ({
                ...prev,
                [tab.key]: {
                  rows,
                  loading: false,
                  error: null,
                  rawData: Array.isArray(res) ? undefined : res,
                  pagination: !Array.isArray(res) && res?.items && Array.isArray(res.items) && typeof res.total === 'number'
                    ? {
                        total: Number(res.total ?? rows.length),
                        page: Number(res.page ?? getTabPagingState(tab).page),
                        limit: Number(res.limit ?? getTabPagingState(tab).pageSize),
                        hasMore: Boolean(res.hasMore),
                        server: true,
                      }
                    : undefined,
                },
              }));
            }
          } catch (err: any) {
            for (const tab of tabs) {
              setTabData(prev => ({
                ...prev,
                [tab.key]: { rows: [], loading: false, error: err?.message ?? 'Failed to load' },
              }));
            }
          }
        })();
      }
    }
  }, [fetchTabData, getTabPagingState, getTabRequestParams, pageDef]);

  const handleRefetch = useCallback(() => {
    const tab = pageDef.tabs.find(t => t.key === activeTab);
    if (tab) fetchTabData(tab, true);
  }, [activeTab, pageDef, fetchTabData]);

  const handleRefreshAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.all(pageDef.tabs.map(t => fetchTabData(t, true)));
    setRefreshing(false);
  }, [pageDef, fetchTabData]);

  // Global KPI data from first tab's raw data (for dashboard)
  const firstTabKey = pageDef.tabs[0]?.key;

  const globalKpiData = firstTabKey ? (tabData[firstTabKey]?.rawData ?? {}) : {};
  const activeTabDef = pageDef.tabs.find((tab) => tab.key === activeTab) ?? null;
  const isMasterDataPage = pageDef.key === 'masterData';
  const activeCreateDialogs = (() => {
    const dialogs = pageDef.dialogs ?? [];
    if (dialogs.length === 0) return [];
    const explicitKeys = activeTabDef?.createDialogKeys
      ?? (activeTabDef?.createDialogKey ? [activeTabDef.createDialogKey] : []);
    if (explicitKeys.length > 0) {
      return dialogs.filter((dialog) => explicitKeys.includes(dialog.key));
    }
    if (dialogs.length === 1) return dialogs;
    return dialogs;
  })();

  const openCreateDialog = useCallback((dialog: DialogDef) => {
    if (dialog.route?.startsWith('/wms/material-requests/new')) {
      setOpenMaterialRequestCreateKey(dialog.key);
      return;
    }
    if (dialog.route) {
      navigate(canonicalizeWmRoute(dialog.route));
      return;
    }
    setOpenDialog(dialog.key);
  }, [navigate]);

  const handlePrimaryAction = useCallback((actionKey: string) => {
    const action = pageDef.primaryActions?.find((candidate) => candidate.key === actionKey);
    if (!action) return;
    if (action.route) {
      navigate(canonicalizeWmRoute(action.route));
      return;
    }
    if (action.dialogKey) {
      const dialog = (pageDef.dialogs ?? []).find((candidate) => candidate.key === action.dialogKey);
      if (dialog) {
        openCreateDialog(dialog);
      }
    }
  }, [navigate, openCreateDialog, pageDef.dialogs, pageDef.primaryActions]);

  const handleOpenPrimaryAdd = useCallback(() => {
    if (activeCreateDialogs.length === 0) return;
    if (activeCreateDialogs.length === 1) {
      openCreateDialog(activeCreateDialogs[0]!);
      return;
    }
    setShowCreateChooser(true);
  }, [activeCreateDialogs, openCreateDialog]);

  // Barcode scanning
  useEffect(() => {
    if (scanning && scanRef.current) scanRef.current.focus();
  }, [scanning]);

  // ── Toolbar ──────────────────────────────────────────────────────────────

  const PageIcon = getIcon(pageDef.icon);
  const runtimeContext = useMemo(() => buildWmsRuntimePageContext(pageDef.key, {
    templateCode: activeWmsTemplate.templateCode,
    template: activeWmsTemplate.template,
    profileKey: activeWmsTemplate.profileKey,
    profile: activeWmsTemplate.profile,
    source: activeWmsTemplate.templateSource,
    matchedAssignment: activeWmsTemplate.matchedAssignment,
    assignments: activeWmsTemplate.assignments,
  }), [
    activeWmsTemplate.assignments,
    activeWmsTemplate.matchedAssignment,
    activeWmsTemplate.profile,
    activeWmsTemplate.profileKey,
    activeWmsTemplate.template,
    activeWmsTemplate.templateCode,
    activeWmsTemplate.templateSource,
    pageDef.key,
  ]);
  const workspaceOverlay = useMemo(
    () => showRuntimeChrome
      ? applyWmsWorkspaceTemplateToTabs(pageDef.tabs, runtimeWorkspace)
      : { tabs: pageDef.tabs, applied: false, appliedLabels: [] },
    [pageDef.tabs, runtimeWorkspace, showRuntimeChrome],
  );
  const reportOverlay = useMemo(
    () => showRuntimeChrome && pageDef.key === 'reports'
      ? applyWmsReportModelToTabs(workspaceOverlay.tabs, runtimeReportModel)
      : { tabs: workspaceOverlay.tabs, applied: false, appliedLabels: [] },
    [pageDef.key, runtimeReportModel, showRuntimeChrome, workspaceOverlay.tabs],
  );
  const billingOverlay = useMemo(
    () => showRuntimeChrome && pageDef.key === 'billing'
      ? applyWmsBillingModelToTabs(reportOverlay.tabs, runtimeBillingModel)
      : { tabs: reportOverlay.tabs, applied: false, appliedLabels: [] },
    [pageDef.key, reportOverlay.tabs, runtimeBillingModel, showRuntimeChrome],
  );
  const renderedTabs = billingOverlay.tabs;

  useEffect(() => {
    if (!showRuntimeChrome) {
      setRuntimeWorkspaceStatus('missing');
      setRuntimeWorkspace(null);
      return;
    }

    let cancelled = false;
    setRuntimeWorkspaceStatus('loading');
    setRuntimeWorkspace(null);

    api.get<{ artifact?: WmsRuntimeWorkspaceTemplate }>(
      `/runtime-artifacts/workspace-templates/${encodeURIComponent(runtimeContext.workspaceTemplateHint)}`,
    ).then((detail) => {
      if (cancelled) return;
      setRuntimeWorkspace(detail.artifact ?? null);
      setRuntimeWorkspaceStatus(detail.artifact ? 'published' : 'missing');
    }).catch(() => {
      if (cancelled) return;
      setRuntimeWorkspace(null);
      setRuntimeWorkspaceStatus('missing');
    });

    return () => {
      cancelled = true;
    };
  }, [runtimeContext.workspaceTemplateHint, showRuntimeChrome]);

  useEffect(() => {
    if (!showRuntimeChrome || pageDef.key !== 'reports') {
      setRuntimeReportStatus('idle');
      setRuntimeReportModel(null);
      return;
    }

    let cancelled = false;
    setRuntimeReportStatus('loading');
    setRuntimeReportModel(null);

    api.get<{ artifact?: WmsRuntimeReportModel }>(
      `/runtime-artifacts/report-models/${encodeURIComponent(runtimeContext.reportModelHint)}`,
    ).then((detail) => {
      if (cancelled) return;
      setRuntimeReportModel(detail.artifact ?? null);
      setRuntimeReportStatus(detail.artifact ? 'published' : 'missing');
    }).catch(() => {
      if (cancelled) return;
      setRuntimeReportModel(null);
      setRuntimeReportStatus('missing');
    });

    return () => {
      cancelled = true;
    };
  }, [pageDef.key, runtimeContext.reportModelHint, showRuntimeChrome]);

  useEffect(() => {
    if (!showRuntimeChrome || pageDef.key !== 'billing') {
      setRuntimeBillingStatus('idle');
      setRuntimeBillingModel(null);
      return;
    }

    let cancelled = false;
    setRuntimeBillingStatus('loading');
    setRuntimeBillingModel(null);

    api.get<{ artifact?: WmsRuntimeBillingModel }>(
      `/runtime-artifacts/billing-models/${encodeURIComponent(runtimeContext.billingModelHint)}`,
    ).then((detail) => {
      if (cancelled) return;
      setRuntimeBillingModel(detail.artifact ?? null);
      setRuntimeBillingStatus(detail.artifact ? 'published' : 'missing');
    }).catch(() => {
      if (cancelled) return;
      setRuntimeBillingModel(null);
      setRuntimeBillingStatus('missing');
    });

    return () => {
      cancelled = true;
    };
  }, [pageDef.key, runtimeContext.billingModelHint, showRuntimeChrome]);

  return (
    <div
      className={`flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${isCompactList ? 'space-y-3' : 'space-y-4'} ${isMobile ? 'p-3' : isWpSimpleOpsPage ? 'p-3' : isCompactList ? 'p-4' : 'p-6'}`}
      data-wms-page={runtimeContext.pageKey}
      data-wms-template={runtimeContext.templateCode}
      data-wms-profile={runtimeContext.profileKey}
      data-wms-workspace-template={runtimeContext.workspaceTemplateHint}
    >
      {/* Header */}
      <div className={isWpSimpleOpsPage ? 'flex flex-wrap items-center justify-between gap-2 border-b pb-2' : isCompactList ? 'space-y-3' : 'flex flex-wrap items-start justify-between gap-3'}>
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            {PageIcon && <PageIcon className="h-5 w-5 shrink-0 text-muted-foreground" />}
            <h1 className="truncate text-lg font-semibold">{pageDef.title}</h1>
          </div>
        </div>
        <div className={`flex flex-wrap items-center gap-2 ${isCompactList && !isWpSimpleOpsPage ? 'justify-start' : 'justify-end'}`}>
          {pageDef.scanEnabled && (
            <Button
              size="sm"
              variant={scanning ? 'default' : 'outline'}
              onClick={() => setScanning(!scanning)}
            >
              <ScanLine className="h-3.5 w-3.5 mr-1" />
              {scanning ? 'Stop Scan' : 'Scan'}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleRefreshAll} disabled={refreshing}>
            <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {importSupported && importDialogType && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowImport(true)} title={importButtonLabel}>
                <Upload className="h-3.5 w-3.5 mr-1" />
                {importButtonLabel}
              </Button>
            </>
          )}
          {pageDef.primaryActions?.map((action) => {
            const ActionIcon = getIcon(action.icon);
            return (
              <Button
                key={action.key}
                size="sm"
                variant={action.variant ?? 'default'}
                onClick={() => handlePrimaryAction(action.key)}
              >
                {ActionIcon && <ActionIcon className="h-3.5 w-3.5 mr-1" />}
                {action.label}
              </Button>
            );
          })}
          {!pageDef.hideAddButton && activeCreateDialogs.length > 0 && (
            <Button size="sm" onClick={handleOpenPrimaryAdd}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          )}
        </div>
      </div>

      {/* Search bar */}
      {searchScope !== 'tab' && !pageDef.hideSearchBox && (
        <div className={`relative ${isWpSimpleOpsPage ? 'hidden' : isCompactList ? 'max-w-[520px]' : ''}`}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={scanRef}
            placeholder={scanning ? 'Scan barcode…' : (pageDef.searchPlaceholder ?? 'Search…')}
            className="pl-9 h-9"
            value={pageSearch}
            onChange={e => setSearchValue(e.target.value)}
          />
        </div>
      )}

      {pageDef.filters && pageDef.filters.length > 0 && (
        <div className={`${isWpSimpleOpsPage ? 'rounded-md border bg-background p-2' : isCompactList ? '' : 'rounded-lg border bg-muted/20 p-3'}`}>
          <div
            className={
              useGridFilterLayout
                ? filterGridClass
                : 'flex flex-wrap items-end gap-2'
            }
          >
          {isWpSimpleOpsPage && !pageDef.hideSearchBox && (
            <div className="space-y-1 md:col-span-2 xl:col-span-2 2xl:col-span-2">
              <Label className="text-xs text-muted-foreground">Keyword</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={scanRef}
                  placeholder={scanning ? 'Scan barcode...' : 'Search no, SKU, project, PO...'}
                  className="h-8 pl-8 text-sm"
                  value={pageSearch}
                  onChange={e => setSearchValue(e.target.value)}
                />
              </div>
            </div>
          )}
          {pageDef.filters.map((filter) => {
            if (filter.type === 'select' || filter.type === 'multiselect') {
              const options = buildFilterOptions(filter, filterOptions);
              if (filter.type === 'multiselect') {
                const selectedValues = (filterValues[filter.field] ?? '').split(',').filter(Boolean);
                const selectedLabels = options
                  .filter((option) => selectedValues.includes(option.value))
                  .map((option) => option.label);
                return (
                  <div key={filter.field} className={filterFieldClass}>
                    <Label className="text-xs text-muted-foreground">{filter.label}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-8 w-full justify-between text-sm font-normal">
                          <span className="truncate">
                            {selectedLabels.length === 0 ? `All ${filter.label}` : selectedLabels.length === 1 ? selectedLabels[0] : `${selectedLabels.length} selected`}
                          </span>
                          <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-[220px] p-2">
                        <div className="max-h-64 space-y-1 overflow-auto">
                          {options.map((option) => {
                            const checked = selectedValues.includes(option.value);
                            return (
                              <label
                                key={option.value}
                                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(nextChecked) => {
                                    setFilterValues((prev) => {
                                      const current = new Set((prev[filter.field] ?? '').split(',').filter(Boolean));
                                      if (nextChecked === true) current.add(option.value);
                                      else current.delete(option.value);
                                      return { ...prev, [filter.field]: Array.from(current).join(',') };
                                    });
                                  }}
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                );
              }
              return (
                <div key={filter.field} className={filterFieldClass}>
                  <Label className="text-xs text-muted-foreground">{filter.label}</Label>
                  <Select
                    value={filterValues[filter.field] || '__all__'}
                    onValueChange={(value) => setFilterValues((prev) => ({
                      ...prev,
                      [filter.field]: value === '__all__' ? '' : value,
                    }))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder={`Select ${filter.label}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All</SelectItem>
                      {options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }

            if (filter.type === 'daterange') {
              return (
                <div key={filter.field} className={useGridFilterLayout ? 'space-y-1 md:col-span-2 xl:col-span-2' : 'flex items-end gap-2'}>
                  {useGridFilterLayout ? (
                    <>
                      <Label className="text-xs text-muted-foreground">{filter.label}</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="date"
                          className="h-8 min-w-0 text-sm"
                          value={filterValues[`${filter.field}From`] ?? ''}
                          onChange={(event) => setFilterValues((prev) => ({
                            ...prev,
                            [`${filter.field}From`]: event.target.value,
                          }))}
                        />
                        <Input
                          type="date"
                          className="h-8 min-w-0 text-sm"
                          value={filterValues[`${filter.field}To`] ?? ''}
                          onChange={(event) => setFilterValues((prev) => ({
                            ...prev,
                            [`${filter.field}To`]: event.target.value,
                          }))}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{filter.label} From</Label>
                        <Input
                          type="date"
                          className="h-8 min-w-[150px] text-sm"
                          value={filterValues[`${filter.field}From`] ?? ''}
                          onChange={(event) => setFilterValues((prev) => ({
                            ...prev,
                            [`${filter.field}From`]: event.target.value,
                          }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{filter.label} To</Label>
                        <Input
                          type="date"
                          className="h-8 min-w-[150px] text-sm"
                          value={filterValues[`${filter.field}To`] ?? ''}
                          onChange={(event) => setFilterValues((prev) => ({
                            ...prev,
                            [`${filter.field}To`]: event.target.value,
                          }))}
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            }

            return (
              <div key={filter.field} className={filterFieldClass}>
                <Label className="text-xs text-muted-foreground">{filter.label}</Label>
                <Input
                  type={filter.type === 'date' ? 'date' : 'text'}
                  className="h-8 text-sm"
                  placeholder={filter.type === 'search' ? `Search ${filter.label.toLowerCase()}...` : undefined}
                  value={filterValues[filter.field] ?? ''}
                  onChange={(event) => setFilterValues((prev) => ({
                    ...prev,
                    [filter.field]: event.target.value,
                  }))}
                />
              </div>
            );
          })}

          <Button
            size="sm"
            variant="ghost"
            className={useGridFilterLayout ? 'justify-self-start self-end' : ''}
            onClick={() => setFilterValues(createInitialFilterValues(pageDef.filters))}
          >
            Clear Filters
          </Button>
          </div>
        </div>
      )}

      {/* Global KPI cards */}
      {!pageDef.hideKpiCards && pageDef.kpiCards && pageDef.kpiCards.length > 0 && (
        <KpiCards cards={pageDef.kpiCards} data={globalKpiData} />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleActiveTabChange} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {renderedTabs.length > 1 && (
          <TabsList className={`w-full justify-start overflow-x-auto ${isWpSimpleOpsPage ? 'rounded-md border bg-background p-1' : ''}`}>
            {renderedTabs.map(tab => {
              const TabIcon = getIcon(tab.icon);
              const tabCount = tabData[tab.key]?.pagination?.total ?? tabData[tab.key]?.rows.length ?? 0;
              return (
                <TabsTrigger key={tab.key} value={tab.key} className="text-xs">
                  {TabIcon && <TabIcon className="h-3.5 w-3.5 mr-1" />}
                  {tab.label} <span className="ml-1 text-[11px] text-muted-foreground">({tabCount})</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        )}

        {renderedTabs.map(tab => {
          const td = tabData[tab.key] ?? { rows: [], loading: true, error: null };
          const displayRows = applyClientFilters(td.rows, pageDef.filters, filterValues, tab);
          const tabSearchValue = getTabSearchValue(tab.key);
          return (
            <TabsContent key={tab.key} value={tab.key} className="mt-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {searchScope === 'tab' && (
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="relative w-full max-w-[420px]">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={activeTab === tab.key ? scanRef : undefined}
                      placeholder={scanning ? 'Scan barcode…' : (pageDef.searchPlaceholder ?? `Search ${tab.label.toLowerCase()}...`)}
                      className="h-9 pl-9"
                      value={tabSearchValue}
                      onChange={(event) => setSearchValue(event.target.value, tab.key)}
                    />
                  </div>
                </div>
              )}
              {isCompactList ? (
                <TabContent
                  pageKey={pageDef.key}
                  tab={tab}
                  statuses={pageDef.statuses}
                  search={tabSearchValue}
                  onRefetch={handleRefetch}
                  requestParams={getTabRequestParams(tab)}
                  data={displayRows}
                  loading={td.loading}
                  error={td.error}
                  hideKpiCards={pageDef.hideKpiCards}
                  simpleMode={isWpSimpleOpsPage}
                  pagination={td.pagination}
                  onPageChange={(page) => setTabPaging((current) => ({
                    ...current,
                    [tab.key]: { page, pageSize: current[tab.key]?.pageSize ?? tab.pagination?.pageSize ?? 50 },
                  }))}
                  onPageSizeChange={(pageSize) => setTabPaging((current) => ({
                    ...current,
                    [tab.key]: { page: 1, pageSize },
                  }))}
                  requestedOpenRowId={requestedOpenRowId}
                  onClearRequestedOpenRowId={clearRequestedOpenRowId}
                />
              ) : (
                <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  <CardContent className="flex min-h-0 min-w-0 flex-1 flex-col p-0">
                    <TabContent
                      pageKey={pageDef.key}
                      tab={tab}
                      statuses={pageDef.statuses}
                      search={tabSearchValue}
                      onRefetch={handleRefetch}
                      requestParams={getTabRequestParams(tab)}
                      data={displayRows}
                      loading={td.loading}
                      error={td.error}
                      hideKpiCards={pageDef.hideKpiCards}
                      simpleMode={isWpSimpleOpsPage}
                      pagination={td.pagination}
                      onPageChange={(page) => setTabPaging((current) => ({
                        ...current,
                        [tab.key]: { page, pageSize: current[tab.key]?.pageSize ?? tab.pagination?.pageSize ?? 50 },
                      }))}
                      onPageSizeChange={(pageSize) => setTabPaging((current) => ({
                        ...current,
                        [tab.key]: { page: 1, pageSize },
                      }))}
                      requestedOpenRowId={requestedOpenRowId}
                      onClearRequestedOpenRowId={clearRequestedOpenRowId}
                    />
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Dialogs */}
      {pageDef.dialogs?.filter(d => !d.route).map(d => (
        <DynamicDialog
          key={d.key}
          def={d}
          open={openDialog === d.key}
          onClose={() => setOpenDialog(null)}
          onSuccess={handleRefreshAll}
          statuses={pageDef.statuses}
        />
      ))}

      <Dialog open={openMaterialRequestCreateKey !== null} onOpenChange={(next) => !next && setOpenMaterialRequestCreateKey(null)}>
        <DialogContent dock="right" className="flex h-[min(90vh,960px)] w-[min(100vw-32px,1320px)] max-w-none overflow-hidden p-0">
          {(() => {
            const dialog = pageDef.dialogs?.find((candidate) => candidate.key === openMaterialRequestCreateKey) ?? null;
            const requestForm = dialog?.route?.includes('requestForm=PLATE')
              ? 'PLATE'
              : dialog?.route?.includes('requestForm=GENERAL')
                ? 'GENERAL'
                : 'PIPE';
            return (
              <WmsMaterialRequestDetailPanel
                embedded
                requestFormPreset={requestForm}
                onClose={() => setOpenMaterialRequestCreateKey(null)}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateChooser} onOpenChange={setShowCreateChooser}>
        <DialogContent dock="right" className="flex max-h-[88vh] w-[min(100vw-32px,1000px)] max-w-none flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>Add</DialogTitle>
            <DialogDescription>
              Select the create action for the current tab: {activeTabDef?.label ?? activeTab}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 overflow-y-auto px-6 py-4 sm:grid-cols-2 xl:grid-cols-3">
            {activeCreateDialogs.map((dialog) => (
              <button
                key={dialog.key}
                type="button"
                className="rounded-lg border bg-background p-4 text-left transition-colors hover:bg-muted/40"
                onClick={() => {
                  setShowCreateChooser(false);
                  openCreateDialog(dialog);
                }}
              >
                <div className="text-sm font-semibold">{dialog.title}</div>
                {dialog.description ? (
                  <div className="mt-1 text-xs text-muted-foreground">{dialog.description}</div>
                ) : null}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* DSL-driven import workflow */}
      {importSupported && importDialogType && (
        <WmsImportWorkflowSheet
          open={showImport}
          onClose={() => {
            setShowImport(false);
            if (searchParams.get('openImport') === '1') {
              const next = new URLSearchParams(searchParams);
              next.delete('openImport');
              next.delete('importType');
              next.delete('attachmentId');
              setSearchParams(next, { replace: true });
            }
          }}
          onSuccess={(completedImportType) => {
            setShowImport(false);
            const successTab = pageDef.importWorkflow?.successTab;
            if (successTab) {
              pendingLocalTabRef.current = successTab;
              setActiveTab(successTab);
              clearSearchState();
              if (searchParams.get('openImport') === '1' || searchParams.get('importType')) {
                const next = new URLSearchParams(searchParams);
                next.delete('openImport');
                next.delete('importType');
                next.delete('attachmentId');
                next.set(tabParamKey, successTab);
                setSearchParams(next, { replace: true });
              }
            } else if (completedImportType === 'purchase-orders') {
              clearSearchState();
            }
            void handleRefreshAll();
          }}
          initialImportType={importDialogType}
          initialAttachmentId={searchParams.get('attachmentId') ?? undefined}
        />
      )}
    </div>
  );
}
